/* =========================================================================
   CO2-Rechner – Frontend (Eingabe, Ausgabe und Variantenvergleich)
   Setzt das Tabellenblatt "Eingabe und Berechnung" der Excel-Vorlage um;
   die Ausgabe folgt dem Tabellenblatt "Ausgabe".
   Institut für Baustatik und Konstruktion, ETH Zürich
   ========================================================================= */
"use strict";

var M = window.CO2Model;
var LOCALE = "de-CH";
var STORE_KEY = "co2rechner.v1";
var MAX_VARIANTS = 8;
/* Höchstens so viele Varianten erscheinen gleichzeitig in Diagramm und Tabellen */
var MAX_SHOWN = 4;

/* ---------------------------------------------------------------------
   1. Eingabemaske – Gliederung wie im Excel-Tabellenblatt
   --------------------------------------------------------------------- */
/* o.info: Erläuterung hinter dem Info-Knopf (HTML) – nur bei Eingaben, die nicht
   selbsterklärend sind, damit die Maske ruhig bleibt. o.link: Verweis auf eine
   andere App, als Pfeil im Kästchen hinter der Beschriftung. o.neg: auch negativ */
function nf(k, l, u, o) { return Object.assign({ k: k, l: l, u: u, type: "num" }, o || {}); }
function sf(k, l, list, o) { return Object.assign({ k: k, l: l, list: list, type: "sel" }, o || {}); }
function bf(k, l) { return { k: k, l: l, type: "bool" }; }
function hd(t) { return { type: "heading", l: t }; }
/* Bestand/Reuse: wiederverwendeter Anteil des darüberstehenden Materials */
function rf(pre) { return nf(pre + "Reuse", "Bestand/Reuse", "%", { max: 100, info: INFO.reuse }); }
function rho(pre) { return nf(pre + "Rho", "Bewehrungsgehalt", U_RHO); }
function note(t) { return { type: "note", l: t }; }

var U_RHO = "kg/m³";

var DECKENRECHNER_URL = "https://concrete.ethz.ch/apps/deckenrechner/";

/* Richtwerte Erstellung aus SIA 390/1:2025, Tabellen 1 (Wohnen) und 3 (Büro) */
var TARGETS = [["Basis", "Umbau", 5], ["Basis", "Neubau", 9], ["Ambitioniert", "Umbau", 4], ["Ambitioniert", "Neubau", 6]];

var INFO = {
  slabGwp: "Treibhausgasemissionen pro m² Deckenfläche, z. B. aus dem Deckenrechner.",
  slabCost: "Kosten pro m² Deckenfläche, z. B. aus dem Deckenrechner. 0: Kosten nicht erfasst.",
  reuse: "Bestand/Reuse: Anteil der Bauteile, die aus dem Bestand übernommen oder wiederverwendet werden. " +
    "Dieser Prozentsatz wird von der eingegebenen Bauteilmenge abgezogen. Emissionen durch Umbau oder Wiederverwendung werden nicht berücksichtigt.",
  target: "Frei wählbar. Richtwerte für die jährlichen Treibhausgasemissionen der Erstellung gem. SIA 390/1:2025, " +
    "bezogen auf ein Jahr und die Energiebezugsfläche A<sub>E</sub> (für Wohnen und Büro gleich):" +
    '<table class="infoTable"><thead><tr><th></th><th>Wohnen</th><th>Büro</th></tr></thead><tbody>' +
    TARGETS.map(function (t) {
      return "<tr><td>" + t[0] + " " + t[1] + "</td><td>" + t[2].toFixed(1) + "</td><td>" + t[2].toFixed(1) + "</td></tr>";
    }).join("") + '</tbody></table><div class="infoUnit">kg CO₂-eq/m²a</div>'
};

/* Auswahllisten mit Option «Benutzerdefiniert»: Einheit des eigenen GWP-Werts,
   entsprechend der Bezugsgrösse, mit der das Material gerechnet wird */
var CUSTOM_UNITS = {
  concrete: "kg CO₂-eq/m³", masonry: "kg CO₂-eq/m³", timber: "kg CO₂-eq/m³",
  sealing: "kg CO₂-eq/m³", insulation: "kg CO₂-eq/m³", excavation: "kg CO₂-eq/m³",
  rebar: "kg CO₂-eq/kg", pitSupport: "kg CO₂-eq/m²", studWalls: "kg CO₂-eq/m²", piles: "kg CO₂-eq/m"
};

function concreteBlock(pre, lenLabel) {
  return [
    sf(pre + "Concrete", "Beton", "concrete"),
    sf(pre + "Rebar", "Bewehrung", "rebar"),
    rho(pre),
    nf(pre + "T", "Wandstärke", "m"),
    nf(pre + "Len", lenLabel, "m"),
    rf(pre)
  ];
}

function columnBlock(pre) {
  return [
    hd("Betonstützen"),
    nf(pre + "ConN", "Anzahl pro Geschoss", "Stück"),
    sf(pre + "Concrete", "Beton", "concrete"),
    nf(pre + "ConB", "Breite", "m"),
    nf(pre + "ConL", "Länge", "m"),
    sf(pre + "Rebar", "Bewehrung", "rebar"),
    rho(pre),
    rf(pre + "Con"),
    hd("Stahlstützen"),
    sf(pre + "SteelProfile", "Profil", "profiles"),
    nf(pre + "SteelN", "Anzahl pro Geschoss", "Stück"),
    rf(pre + "Steel"),
    hd("Holzstützen"),
    sf(pre + "Timber", "Holz", "timber"),
    nf(pre + "TimberN", "Anzahl pro Geschoss", "Stück"),
    nf(pre + "TimberB", "Breite", "m"),
    nf(pre + "TimberL", "Länge", "m"),
    rf(pre + "Timber")
  ];
}

var SECTIONS = [
  { id: "general", title: "Allgemeine Angaben", open: true, groups: [
    { title: "Obergeschosse", fields: [
      nf("ogL", "Länge", "m"), nf("ogB", "Breite", "m"), nf("ogH", "Geschosshöhe", "m"),
      nf("ogN", "Geschossanzahl inkl. EG", "Geschosse")
    ]},
    { title: "Untergeschosse", fields: [
      nf("ugL", "Länge", "m"), nf("ugB", "Breite", "m"), nf("ugH", "Geschosshöhe", "m"),
      nf("ugN", "Geschossanzahl", "Geschosse"),
      nf("ugOffset", "Versatz zwischen Decke über UG und OK Terrain", "m", { neg: true, info:
        "Der Höhenversatz zwischen der Deckenoberkante der Decke über dem Untergeschoss und der projektierten Terrainoberkante.<br>" +
        "+ Die Decke über UG liegt höher als OK Terrain<br>" +
        "− Die Decke über UG liegt tiefer als OK Terrain<br>" +
        "Tiefe von Baugrubenverbau und Aushub sowie Höhe von Dämmung und Abdichtung der Untergeschosswände passen sich an." })
    ]},
    { title: "Abmessungen", viz: true, fields: [] },
    { title: "Bezugsgrössen der Auswertung", fields: [
      nf("ebf", "Energiebezugsfläche AE / EBF", "m²", { info:
        "Die Energiebezugsfläche ist, gem. SIA 380:2022, Ziffer 3.2, die Summe aller ober- und unterirdischen Geschossflächen, " +
        "die innerhalb der thermischen Gebäudehülle liegen und für deren Nutzung ein Konditionieren notwendig ist." }),
      nf("lifespan", "Amortisationszeit", "Jahre", { info:
        "Die Amortisationszeit entspricht der in diesem Merkblatt festgelegten Nutzungsdauer von Bauteilen. " +
        "Für den Rohbau beträgt sie 60 Jahre gem. SIA Merkblatt 2032:2020, Anhang C." }),
      nf("target", "Zielwert", "kg CO₂-eq/m²a", { info: INFO.target })
    ]}
  ]},
  { id: "slabs", title: "Decken und Dach", groups: [
    { title: "Dach", comp: "roof", fields: [
      nf("roofGwpM2", "GWP aus Deckenrechner", "kg CO₂-eq/m²", { info: INFO.slabGwp, link: DECKENRECHNER_URL }),
      nf("roofCostM2", "Kosten aus Deckenrechner", "CHF/m²", { info: INFO.slabCost }),
      rf("roof")
    ]},
    { title: "Decken über Terrain", comp: "slabsAbove", fields: [
      nf("slabAboveGwpM2", "GWP aus Deckenrechner", "kg CO₂-eq/m²", { info: INFO.slabGwp, link: DECKENRECHNER_URL }),
      nf("slabAboveCostM2", "Kosten aus Deckenrechner", "CHF/m²", { info: INFO.slabCost }),
      rf("slabAbove")
    ]},
    { title: "Decken unter Terrain", comp: "slabsBelow", fields: [
      sf("slabBelowConcrete", "Beton", "concrete"),
      sf("slabBelowRebar", "Bewehrung", "rebar"),
      rho("slabBelow"),
      nf("slabBelowT", "Deckenstärke", "m"),
      rf("slabBelow")
    ]},
    { title: "Dach unter Terrain", comp: "roofBelow", fields: [
      hd("Beton"),
      sf("roofBelowConcrete", "Beton", "concrete"),
      sf("roofBelowRebar", "Bewehrung", "rebar"),
      rho("roofBelow"),
      nf("roofBelowT", "Deckenstärke", "m"),
      rf("roofBelow"),
      hd("Abdichtung"),
      sf("roofBelowSeal", "Abdichtung", "sealing"),
      nf("roofBelowSealMm", "Abdichtungsdicke", "mm"),
      rf("roofBelowSeal"),
      note("Fällt nur an, wenn die Untergeschossfläche grösser ist als die Obergeschossfläche.")
    ]}
  ]},
  { id: "walls", title: "Wände", groups: [
    { title: "Wände tragend über Terrain", comp: "wallsAbove", fields: [
      hd("Beton")
    ].concat(concreteBlock("wallAbove", "Gesamtlänge pro Geschoss"), [
      hd("Mauerwerk"),
      sf("wallAboveMasonry", "Mauerwerk", "masonry"),
      nf("wallAboveMasonryT", "Wandstärke", "m"),
      nf("wallAboveMasonryLen", "Gesamtlänge pro Geschoss", "m"),
      rf("wallAboveMasonry"),
      hd("Holzständer"),
      sf("wallAboveStud", "Holzständer (Lignumdata)", "studWalls"),
      nf("wallAboveStudLen", "Gesamtlänge pro Geschoss", "m"),
      rf("wallAboveStud"),
      note("Brandschutz ist separat zu prüfen.")
    ])},
    { title: "Wände tragend unter Terrain", comp: "wallsBelow", fields: [
      hd("Beton")
    ].concat(concreteBlock("wallBelow", "Gesamtlänge Innenwände pro Geschoss").map(function (f) {
      if (f.k === "wallBelowLen") f.k = "wallBelowInnerLen";
      return f;
    }), [
      hd("Dämmung und Abdichtung"),
      sf("wallBelowIns", "Dämmung", "insulation"),
      nf("wallBelowInsT", "Dämmstärke", "m"),
      sf("wallBelowSeal", "Abdichtung", "sealing"),
      nf("wallBelowSealMm", "Abdichtungsdicke", "mm"),
      rf("wallBelowIns"),
      note("Aussenwände entlang dem Umfang der Untergeschosse, zuzüglich Innenwände. Abdichtung nur unter Terrain.")
    ])}
  ]},
  { id: "columns", title: "Stützen", groups: [
    { title: "Stützen tragend über Terrain", comp: "colsAbove", fields: columnBlock("colAbove") },
    { title: "Stützen tragend unter Terrain", comp: "colsBelow", fields: columnBlock("colBelow") }
  ]},
  { id: "base", title: "Bodenplatte", groups: [
    { title: "Bodenplatte (ohne Sauberkeitsschicht)", comp: "baseSlab", fields: [
      hd("Beton"),
      sf("baseConcrete", "Material", "concrete"),
      sf("baseRebar", "Bewehrung", "rebar"),
      rho("base"),
      nf("baseT", "Plattenstärke", "m"),
      rf("base"),
      hd("Dämmung und Abdichtung"),
      sf("baseIns", "Dämmung", "insulation"),
      nf("baseInsT", "Dämmstärke", "m"),
      sf("baseSeal", "Abdichtung", "sealing"),
      nf("baseSealMm", "Abdichtungsdicke", "mm"),
      rf("baseIns")
    ]}
  ]},
  { id: "foundation", title: "Fundation", groups: [
    { title: "Fundation", comp: "foundation", fields: [
      hd("Beton"),
      sf("fndConcrete", "Material", "concrete"),
      sf("fndRebar", "Bewehrung", "rebar"),
      hd("Einzelfundamente"),
      nf("padL", "Länge", "m"), nf("padB", "Breite", "m"), nf("padD", "Dicke unter BP", "m"),
      rho("pad"),
      nf("padN", "Anzahl", "Stück"),
      rf("pad"),
      hd("Streifenfundamente"),
      nf("stripB", "Breite", "m"), nf("stripD", "Dicke unter BP", "m"),
      rho("strip"),
      nf("stripLen", "Laufmeter", "m"),
      rf("strip"),
      hd("Bohrpfähle"),
      sf("pileType", "Art", "piles"),
      nf("pileLen", "Länge", "m"),
      nf("pileN", "Anzahl", "Stück"),
      rf("pile")
    ]}
  ]},
  { id: "pit", title: "Baugrubenverbau und Aushub", groups: [
    { title: "Baugrubenverbau", comp: "pitSupport", fields: [
      bf("pitPresent", "Vorhanden"),
      sf("pitType", "Art", "pitSupport"),
      nf("pitTrenchW", "Arbeitsgrabenbreite", "m", { info:
        "Arbeitsraum zwischen Baugrubenwand und Aussenwand der Untergeschosse; vergrössert Verbau und Aushub. 0 m: ohne Arbeitsgraben." })
    ]},
    { title: "Aushub (ohne Fundationsaushub)", comp: "excavation", fields: [
      sf("excavationType", "Art", "excavation"),
      note("Grundfläche inkl. Arbeitsgraben und Dämmung der Untergeschosswände; Tiefe ab OK Terrain bis Unterkante Dämmung unter der Bodenplatte.")
    ]}
  ]}
];

/* Zu jeder Auswahl mit «Benutzerdefiniert» gehört ein GWP-Feld direkt darunter,
   das nur bei dieser Wahl erscheint */
SECTIONS.forEach(function (s) {
  s.groups.forEach(function (g) {
    g.fields = g.fields.reduce(function (out, f) {
      out.push(f);
      if (f.type === "sel" && CUSTOM_UNITS[f.list]) {
        var l = f.l === "Art" ? "GWP" : "GWP " + f.l.replace(/ \(.*\)$/, "");
        out.push(nf(f.k + "Gwp", l, CUSTOM_UNITS[f.list], { customFor: f.k }));
      }
      return out;
    }, []);
  });
});

var FIELDS = {};
SECTIONS.forEach(function (s) {
  s.groups.forEach(function (g) {
    g.fields.forEach(function (f) {
      if (f.k) { f.section = s; f.group = g; FIELDS[f.k] = f; }
    });
  });
});

/* ---------------------------------------------------------------------
   2. Farben – Bauteile in Farbfamilien ihrer Hauptgruppe
   --------------------------------------------------------------------- */
var INK = { primary: "#0b0b0b", secondary: "#52514e", muted: "#8b8a85", grid: "#e6e5e1", surface: "#ffffff" };

/* Stapelreihenfolge von unten nach oben – entspricht der Lage im Gebäude */
var STACK_ORDER = [
  "excavation", "pitSupport", "foundation", "baseSlab", "colsBelow", "wallsBelow",
  "roofBelow", "slabsBelow", "colsAbove", "wallsAbove", "slabsAbove", "roof"
];

/* Validierte kategoriale Palette (Hellmodus) in fester Reihenfolge. Die zwölf
   Bauteile tragen die Farbe ihrer Hauptgruppe als geordnete Abstufung:
   innerhalb jeder Gruppe unten dunkel, nach oben heller (validierte Rampen). */
var SERIES = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

var COLORS = {
  components: {
    excavation: "#b07800", pitSupport: SERIES[3],
    foundation: SERIES[2],
    baseSlab: "#7a2e10", colsBelow: "#a33f17", wallsBelow: "#cf521f", roofBelow: SERIES[1], slabsBelow: "#f08c60",
    colsAbove: "#104281", wallsAbove: "#256abf", slabsAbove: "#3987e5", roof: "#86b6ef"
  },
  mainGroups: { O: SERIES[0], U: SERIES[1], F: SERIES[2], B: SERIES[3] },
  categories: { H: SERIES[0], V: SERIES[1], B: SERIES[2], VO: SERIES[3] }
};

/* Breite der Legendenspalte rechts vom Diagramm (Platz für die längste Bauteilbezeichnung) */
var LEGEND_W = 310;

/* ---------------------------------------------------------------------
   3. Hilfsfunktionen
   --------------------------------------------------------------------- */
function el(id) { return document.getElementById(id); }

function esc(s) {
  return String(s).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
  });
}

function fmt(v, dec) {
  if (v === undefined || v === null || !isFinite(v)) return "–";
  return Number(v).toLocaleString(LOCALE, { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/* Zahl mit so vielen Nachkommastellen wie nötig (max. 4) */
function fmtAuto(v) {
  if (v === undefined || v === null || !isFinite(v)) return "–";
  return Number(v).toLocaleString(LOCALE, { maximumFractionDigits: 4 });
}

function fmtSigned(v, dec) {
  if (!isFinite(v)) return "–";
  var s = fmt(Math.abs(v), dec);
  return (v > 0 ? "+" : v < 0 ? "−" : "±") + s;
}

function clone(o) { return JSON.parse(JSON.stringify(o)); }

function fieldValueText(f, v) {
  if (f.type === "bool") return v ? "ja" : "nein";
  if (f.type === "num") return fmtAuto(v) + " " + f.u;
  return String(v);
}

function listItems(list) {
  var items;
  if (list === "profiles") items = DATA.profiles.map(function (p) { return { v: p.name, ok: true }; });
  else if (list === "studWalls") items = DATA.studWalls.map(function (p) { return { v: p.name, ok: true }; });
  else items = DATA.lists[list].map(function (n) {
    var m = DATA.materials[n];
    /* Verbau, Pfähle und Aushub werden pro m², m bzw. m³ gerechnet –
       diese brauchen keine Rohdichte */
    var perUnit = ["pitSupport", "piles", "excavation"].indexOf(list) !== -1;
    return { v: n, ok: !!m && m.gwp !== null && (perUnit || m.density !== null) };
  });
  if (CUSTOM_UNITS[list]) items.push({ v: M.CUSTOM, ok: true });
  return items;
}

/* GWP eines Listeneintrags in der Einheit des benutzerdefinierten Felds
   (Vorbelegung beim Wechsel auf «Benutzerdefiniert») */
function listGwp(list, name) {
  if (list === "studWalls") {
    var s = DATA.studWalls.filter(function (x) { return x.name === name; })[0];
    return s ? s.gwpM2 : 0;
  }
  var m = DATA.materials[name];
  if (!m || m.gwp === null) return 0;
  return CUSTOM_UNITS[list] === "kg CO₂-eq/m³" && m.density !== null && list !== "excavation" ? m.gwp * m.density : m.gwp;
}

/* Benutzerdefiniertes GWP-Feld, dessen Auswahl nicht auf «Benutzerdefiniert» steht */
function customInactive(f, inputs) {
  return !!f.customFor && inputs[f.customFor] !== M.CUSTOM;
}

/* ---------------------------------------------------------------------
   4. Zustand und Speicherung im Browser
   --------------------------------------------------------------------- */
var DATA = null;
var state = {
  variants: [],     // { name, inputs, shown }
  active: 0,
  ref: 0,
  grouping: "components",
  onlyDiff: true,
  tab: "general",   // gewählter Reiter der Eingabe
  collapsed: {},    // zugeklappte Bereiche: Eingabe, Ausgabe, Auswertung, Kosten, Variantenvergleich
  sort: null,       // Sortierung der Bauteiltabelle: { key: "t0" | "p0" | Spalte, dir: 1 | -1 }
  prices: null      // Kostenansätze, für alle Varianten gemeinsam
};
var results = [];   // Resultate je Variante

/* Standardwerte der Excel-Vorlage; Bestand/Reuse und Kosten sind dort nicht vorgesehen (0).
   Die Energiebezugsfläche folgt der gesamten Geschossfläche, bis sie überschrieben wird. */
function defaults() {
  var d = clone(DATA.defaults);
  M.REUSE.forEach(function (e) { d[e.key] = 0; });
  d.roofCostM2 = 0;
  d.slabAboveCostM2 = 0;
  Object.keys(FIELDS).forEach(function (k) { if (FIELDS[k].customFor) d[k] = 0; });
  d.ebfAuto = true;
  syncEbf(d);
  return d;
}

/* Gesamte Geschossfläche: Länge × Breite × Geschossanzahl der Ober- und Untergeschosse */
function grossArea(x) {
  return Math.round((pos(x.ogL) * pos(x.ogB) * pos(x.ogN) + pos(x.ugL) * pos(x.ugB) * pos(x.ugN)) * 100) / 100;
}

function syncEbf(inp) {
  if (inp.ebfAuto) inp.ebf = grossArea(inp);
}

function nextName() {
  var used = state.variants.map(function (v) { return v.name; });
  for (var i = 0; i < 26; i++) {
    var n = "Variante " + String.fromCharCode(65 + i);
    if (used.indexOf(n) === -1) return n;
  }
  return "Variante " + (state.variants.length + 1);
}

/* Gespeicherte Eingaben gegen die aktuelle Datenbasis prüfen */
/* Material zur KBOB-UUID – findet umbenannte Materialien wieder */
function materialByUuid(uuid) {
  if (!uuid) return null;
  for (var n in DATA.materials) if (DATA.materials[n].uuid === uuid) return n;
  return null;
}

/* UUIDs der gewählten Materialien, gespeichert neben den Bezeichnungen */
function materialIds(inputs) {
  var ids = {};
  Object.keys(FIELDS).forEach(function (k) {
    var m = FIELDS[k].type === "sel" && DATA.materials[inputs[k]];
    if (m && m.uuid) ids[k] = m.uuid;
  });
  return ids;
}

/* issues (optional) sammelt, welche Eingaben nicht übernommen werden konnten:
   { k, kind: "new" | "invalid" | "renamed" | "unavailable", was }
   ids (optional): gespeicherte UUIDs der Materialien, siehe materialIds() */
function sanitize(inputs, issues, ids) {
  var d = defaults(), out = {};
  issues = issues || [];
  ids = ids || {};
  /* frühere Fassung: Arbeitsgraben ja/nein mit fester Breite 0.8 m */
  if (inputs && typeof inputs.pitTrench === "boolean" && !inputs.hasOwnProperty("pitTrenchW")) {
    inputs.pitTrenchW = inputs.pitTrench ? 0.8 : 0;
  }
  /* frühere Fassung: Energiebezugsfläche immer von Hand eingegeben */
  if (inputs && inputs.hasOwnProperty("ebf") && !inputs.hasOwnProperty("ebfAuto")) inputs.ebfAuto = false;
  /* frühere Fassung: Abdichtungsdicken in m */
  ["roofBelowSeal", "wallBelowSeal", "baseSeal"].forEach(function (k) {
    if (inputs && inputs.hasOwnProperty(k + "T") && !inputs.hasOwnProperty(k + "Mm")) {
      var t = parseFloat(inputs[k + "T"]);
      inputs[k + "Mm"] = isFinite(t) ? Math.round(t * 1e6) / 1e3 : inputs[k + "T"];
    }
  });
  Object.keys(d).forEach(function (k) {
    var f = FIELDS[k];
    if (!inputs || !inputs.hasOwnProperty(k)) {
      /* Fehlende benutzerdefinierte GWP-Felder sind bedeutungslos, solange die Auswahl nicht darauf steht */
      if (!(f && f.customFor && inputs && inputs[f.customFor] !== M.CUSTOM)) issues.push({ k: k, kind: "new" });
      out[k] = d[k];
      return;
    }
    var v = inputs[k];
    if (f && f.type === "num" && !isFinite(parseFloat(v))) {
      issues.push({ k: k, kind: "invalid", was: v });
      v = d[k];
    }
    if ((f && f.type === "bool") || k === "ebfAuto") v = !!v;
    var inList = function (name) { return listItems(f.list).some(function (it) { return it.v === name; }); };
    if (f && f.type === "sel" && !inList(v)) {
      var renamed = materialByUuid(ids[k]);
      if (renamed && inList(renamed)) {
        issues.push({ k: k, kind: "renamed", was: v });
        v = renamed;
      } else {
        issues.push({ k: k, kind: "unavailable", was: v });
        v = d[k];
      }
    }
    out[k] = v;
  });
  return out;
}

/* Kostenansätze: leer (null) heisst ohne Ansatz, d. h. Kosten nicht erfasst */
function defaultPrices() {
  var p = {};
  M.COST_ITEMS.forEach(function (it) { p[it.key] = it.def; });
  return p;
}

function sanitizePrices(p) {
  var out = defaultPrices();
  if (!p || typeof p !== "object") return out;
  Object.keys(out).forEach(function (k) {
    if (!p.hasOwnProperty(k)) return;
    var v = p[k] === null ? null : parseFloat(p[k]);
    if (v === null || (isFinite(v) && v >= 0)) out[k] = v;
  });
  return out;
}

/* Gesamter Zustand – im Browser gespeichert und als Datei herunterladbar */
function snapshot() {
  return {
    app: "co2rechner", version: 1, source: DATA.source, kbob: DATA.kbob,
    /* Resultate beim Speichern – nur zum Vergleich beim Öffnen, gerechnet wird immer neu */
    variants: state.variants.map(function (v, i) {
      return Object.assign({}, v, {
        ids: materialIds(v.inputs),
        saved: results[i] ? { total: results[i].total, epf: results[i].eval.epf } : undefined
      });
    }),
    active: state.active, ref: state.ref, grouping: state.grouping, onlyDiff: state.onlyDiff, tab: state.tab,
    collapsed: state.collapsed, prices: state.prices
  };
}

function save() {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(snapshot()));
  } catch (e) { /* Speicher nicht verfügbar – ohne Persistenz weiterarbeiten */ }
}

function load() {
  try {
    return applySnapshot(JSON.parse(localStorage.getItem(STORE_KEY) || "null"));
  } catch (e) {
    return false;
  }
}

/* Übernimmt einen gespeicherten Zustand; Eingaben werden gegen die
   aktuelle Datenbasis geprüft. false, wenn s kein gültiger Zustand ist. */
/* issues (optional): je Variante die Liste aus sanitize() */
function applySnapshot(s, issues) {
  if (!s || !Array.isArray(s.variants) || !s.variants.length) return false;
  if (!s.variants.every(function (v) { return v && typeof v.inputs === "object"; })) return false;
  state.variants = s.variants.slice(0, MAX_VARIANTS).map(function (v, i) {
    var list = [];
    if (issues) issues[i] = list;
    return { name: String(v.name || "Variante"), inputs: sanitize(v.inputs, list, v.ids), shown: v.shown !== false };
  });
  var last = state.variants.length - 1;
  state.active = Math.min(Math.max(0, s.active | 0), last);
  state.ref = Math.min(Math.max(0, s.ref | 0), last);
  if (COLORS[s.grouping]) state.grouping = s.grouping;
  if (typeof s.onlyDiff === "boolean") state.onlyDiff = s.onlyDiff;
  if (SECTIONS.some(function (x) { return x.id === s.tab; })) state.tab = s.tab;
  state.collapsed = {};
  if (s.collapsed && typeof s.collapsed === "object") {
    PANES.forEach(function (p) { if (s.collapsed[p] === true) state.collapsed[p] = true; });
  }
  state.prices = sanitizePrices(s.prices);
  normalizeShown();
  return true;
}

/* Referenz ist immer sichtbar; höchstens MAX_SHOWN Varianten im Vergleich */
function normalizeShown() {
  state.variants[state.ref].shown = true;
  var n = 1;
  state.variants.forEach(function (v, i) {
    if (i === state.ref || !v.shown) return;
    if (n < MAX_SHOWN) n++;
    else v.shown = false;
  });
}

function shownCount() {
  return state.variants.filter(function (v) { return v.shown; }).length;
}

function resetAll() {
  state.variants = [{ name: "Variante A", inputs: defaults(), shown: true }];
  state.active = 0;
  state.ref = 0;
  state.prices = defaultPrices();
}

function activeVariant() { return state.variants[state.active]; }
function refVariant() { return state.variants[state.ref]; }

/* ---------------------------------------------------------------------
   5. Eingabemaske aufbauen
   --------------------------------------------------------------------- */
function renderField(f) {
  if (f.type === "heading") return '<div class="fHeading">' + esc(f.l) + "</div>";
  if (f.type === "note") return '<div class="fNote">' + esc(f.l) + "</div>";

  var id = "f_" + f.k, ctl;
  if (f.type === "num") {
    ctl = '<input type="number" step="any"' + (f.neg ? "" : ' min="0"') + (f.max ? ' max="' + f.max + '"' : "") +
      ' class="form-control form-control-sm fInput" id="' + id + '" data-k="' + f.k + '">';
  } else if (f.type === "bool") {
    ctl = '<select class="custom-select custom-select-sm fInput" id="' + id + '" data-k="' + f.k + '">' +
      '<option value="true">ja</option><option value="false">nein</option></select>';
  } else {
    ctl = '<select class="custom-select custom-select-sm fInput" id="' + id + '" data-k="' + f.k + '">' +
      listItems(f.list).map(function (it) {
        return '<option value="' + esc(it.v) + '"' + (it.ok ? "" : " disabled") + ">" +
          esc(it.v) + (it.ok ? "" : " (keine GWP-Daten)") + "</option>";
      }).join("") + "</select>";
  }
  var unit = f.type === "num" ? '<span class="fUnit">' + esc(f.u) + "</span>" : '<span class="fUnit"></span>';
  /* Info-Knopf als Link mit tabindex, damit der Fokus-Auslöser des Popovers auch in Safari greift */
  var info = f.info ? ' <a tabindex="0" role="button" class="fInfo" data-toggle="popover" data-trigger="focus" data-placement="top"' +
    ' data-html="true" data-content="' + esc(f.info) + '" aria-label="Info zu ' + esc(f.l) + '">' +
    '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7.25"/><circle class="fInfoDot" cx="8" cy="4.6" r="1"/>' +
    '<path d="M8 7v5"/></svg></a>' : "";
  if (f.link) {
    info += ' <a class="fInfo fLink" href="' + esc(f.link) + '" target="_blank" rel="noopener" title="Deckenrechner öffnen">' +
      '<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="0.75" y="0.75" width="14.5" height="14.5" rx="1.5"/>' +
      '<path d="M5.5 10.5l5-5M6.5 5.5h4v4"/></svg></a>';
  }
  /* fHalf: kurze Eingaben stehen in den Bauteilblöcken paarweise nebeneinander;
     mit langer Einheit (kg CO₂-eq/…) bliebe dem Feld zu wenig Platz */
  var half = f.type === "bool" || (f.type === "num" && f.u.length <= 6);
  /* Trennstelle zwischen den Wortteilen, falls die Beschriftung umbrechen muss */
  var label = esc(f.l).replace(/^(Bewehrungs|Arbeitsgraben|Abdichtungs)(?=\S)/, "$1&shy;");
  var html = '<div class="fRow' + (f.customFor ? " fCustom" : "") + (half ? " fHalf" : "") + '" data-row="' + f.k + '"><label class="fLabel" for="' + id + '">' + label +
    info + '</label><div class="fCtl">' + ctl + "</div>" + unit + "</div>";
  if (f.type === "sel") html += '<div class="fLookup" data-lk="' + f.k + '"></div>';
  if (f.k === "ebf") html += '<div class="fLookup" id="ebfHint"></div>';
  return html;
}

/* Felder einer Gruppe in Blöcke teilen, je Zwischenüberschrift einer */
function fieldBlocks(fields) {
  var out = [], cur = null;
  fields.forEach(function (f) {
    if (f.type === "heading" || !cur) out.push(cur = { title: f.type === "heading" ? f.l : "", fields: [] });
    if (f.type !== "heading") cur.fields.push(f);
  });
  return out;
}

function groupHead(g) {
  return '<div class="grpHead"><span class="grpTitle">' + esc(g.title) + "</span>" +
    '<span class="secChanged" data-grp="' + g.comp + '"></span>' +
    '<span class="grpTotal" data-grp="' + g.comp + '"></span></div>';
}

/* Bauteil als Band: Materialblöcke nebeneinander, Resultat als letzte Spalte.
   Alle Spalten sind gleich breit, so stehen gleiche Blöcke untereinander;
   schmale Bänder teilen sich eine Zeile (--span: Anzahl Spalten) */
function renderBand(g) {
  var blocks = fieldBlocks(g.fields);
  var html = '<div class="grpBand" style="--span:' + (blocks.length + 1) + '">' + groupHead(g) + '<div class="blkRow">';
  blocks.forEach(function (b) {
    html += '<div class="blk">' + (b.title ? '<div class="blkTitle">' + esc(b.title) + "</div>" : "") +
      b.fields.map(renderField).join("") + "</div>";
  });
  return html + '<div class="blk blkResult"><div class="blkTitle">Resultat <span class="thUnit">[kg CO₂-eq]</span></div><div class="fResult" data-comp="' + g.comp + '"></div></div></div></div>';
}

/* Gruppen ohne Bauteil (Allgemeine Angaben): Gruppen nebeneinander, Beschriftung links */
function renderGroup(g) {
  var html = '<div class="grpBox' + (g.viz ? " grpViz" : "") + '">' + (g.title ? '<div class="grpTitle">' + esc(g.title) + "</div>" : "");
  if (g.viz) html += '<div id="bldViz"></div>';
  return html + g.fields.map(renderField).join("") + "</div>";
}

/* Abschnitte als Reiter nebeneinander (wie die Tabellenblätter in Excel); im Reiter
   Summe und Änderungen, darunter der gewählte Abschnitt */
function buildInputPanel() {
  var tabs = "", panes = "";
  SECTIONS.forEach(function (s) {
    var on = s.id === state.tab;
    var bands = s.groups.some(function (g) { return g.comp; });
    tabs += '<button type="button" role="tab" class="secTab' + (on ? " active" : "") + '" data-tab="' + s.id +
      '" aria-selected="' + on + '" aria-controls="sec_' + s.id + '">' +
      '<span class="secTitle">' + esc(s.title) + "</span>" +
      '<span class="secMeta"><span class="secTotal" data-sec="' + s.id + '"></span>' +
      '<span class="secChanged" data-sec="' + s.id + '"></span></span></button>';
    panes += '<div class="secPane" role="tabpanel" id="sec_' + s.id + '"' + (on ? "" : " hidden") + '>' +
      '<div class="' + (bands ? "bandFlow" : "grpGrid") + '">' + s.groups.map(bands ? renderBand : renderGroup).join("") + "</div></div>";
  });
  el("inputPanel").innerHTML = '<div class="secTabs" role="tablist">' + tabs + "</div>" + panes;
  /* Inhalte stammen nur aus SECTIONS; ohne Sanitizer, damit Tabellen erhalten bleiben */
  $("#inputPanel [data-toggle=popover]").popover({ sanitize: false });
}

function selectTab(id) {
  state.tab = id;
  document.querySelectorAll("#inputPanel .secTab").forEach(function (t) {
    var on = t.getAttribute("data-tab") === id;
    t.classList.toggle("active", on);
    t.setAttribute("aria-selected", on);
  });
  document.querySelectorAll("#inputPanel .secPane").forEach(function (p) {
    p.hidden = p.id !== "sec_" + id;
  });
  $("#inputPanel [data-toggle=popover]").popover("hide");
  save();
}

/* Eingabewerte der aktiven Variante in die Maske schreiben */
function fillInputs() {
  var inp = activeVariant().inputs;
  Object.keys(FIELDS).forEach(function (k) {
    var node = el("f_" + k);
    if (!node) return;
    node.value = FIELDS[k].type === "bool" ? String(!!inp[k]) : inp[k];
  });
  el("activeName").textContent = activeVariant().name;
}

/* Nachschlagewerte als eine ruhige Zeile; die Bezeichnungen stehen im Tooltip */
function lookupHtml(parts) {
  var title = parts.map(function (p) { return p[0] + ": " + p[1]; }).join("\n");
  return '<span title="' + title + '">' + parts.map(function (p) { return p[1]; }).join(" · ") + "</span>";
}

function materialLookup(name) {
  var m = DATA.materials[name];
  if (!m) return "";
  var dens = m.densityText && m.densityText !== String(m.density)
    ? esc(m.densityText) + " (Mittel " + fmtAuto(m.density) + ")" : fmtAuto(m.density);
  var parts = [];
  if (m.density !== null) parts.push(["Rohdichte", dens + " " + U_RHO]);
  parts.push(["GWP", (m.gwp === null ? "–" : fmtAuto(m.gwp)) + " kg CO₂-eq/" + esc(m.unit || "Einheit")]);
  return lookupHtml(parts);
}

function updateLookups() {
  var inp = activeVariant().inputs;
  /* Energiebezugsfläche: folgt der Geschossfläche, bis sie überschrieben wird */
  var ebf = el("f_ebf");
  if (inp.ebfAuto && document.activeElement !== ebf) ebf.value = inp.ebf;
  el("ebfHint").innerHTML = inp.ebfAuto
    ? "Gesamte Geschossfläche (L × B × Geschosse, OG + UG)"
    : '<button type="button" class="btn btn-link btn-sm p-0 align-baseline" id="btnEbfAuto">Gesamte Geschossfläche übernehmen (' +
      fmtAuto(grossArea(inp)) + " m²)</button>";
  document.querySelectorAll("#inputPanel .fCustom").forEach(function (row) {
    row.hidden = customInactive(FIELDS[row.getAttribute("data-row")], inp);
  });
  document.querySelectorAll("#inputPanel .fLookup[data-lk]").forEach(function (node) {
    var f = FIELDS[node.getAttribute("data-lk")], v = inp[f.k];
    if (v === M.CUSTOM) {
      node.innerHTML = "";
    } else if (f.list === "profiles") {
      var p = DATA.profiles.filter(function (x) { return x.name === v; })[0];
      var m = DATA.materials[DATA.steelProfileMaterial];
      node.innerHTML = lookupHtml([
        ["Gewicht", (p ? fmtAuto(p.kgPerM) : "–") + " kg/m"],
        [DATA.steelProfileMaterial, (m ? fmtAuto(m.gwp) : "–") + " kg CO₂-eq/kg"]
      ]);
    } else if (f.list === "studWalls") {
      var s = DATA.studWalls.filter(function (x) { return x.name === v; })[0];
      node.innerHTML = lookupHtml([["GWP", (s ? fmtAuto(s.gwpM2) : "–") + " kg CO₂-eq/m²"]]);
    } else {
      node.innerHTML = materialLookup(v);
    }
  });
}

function resultRow(label, kg, cls) {
  return '<div class="rRow' + (cls ? " " + cls : "") + '"><span>' + label + '</span><span class="rVal">' + fmt(kg, 0) +
    '</span><span class="rUnit">kg CO₂-eq</span></div>';
}

/* Resultate je Bauteil, Abweichungen gegenüber der Referenz, Abschnittssummen */
function updateInputDecorations() {
  var res = results[state.active];
  var inp = activeVariant().inputs;
  var ref = refVariant().inputs;
  var isRef = state.active === state.ref;

  document.querySelectorAll("#inputPanel .fResult").forEach(function (node) {
    var c = res.components[node.getAttribute("data-comp")];
    var html = "";
    (c.parts || []).forEach(function (p) { html += resultRow(esc(p.label), p.kg); });
    if (c.reuse.length) {
      if (c.parts) html += resultRow("Summe", c.gross, "rSub");
      c.reuse.forEach(function (e) {
        html += resultRow("Bestand/Reuse " + esc(e.label) + " " + fmtAuto(e.pct) + " %", -e.kg, "rReuse");
      });
    }
    var name = M.COMPONENTS.filter(function (x) { return x.key === node.getAttribute("data-comp"); })[0].name;
    html += resultRow("Total", c.kg, "rTotal");
    node.innerHTML = html;
  });

  var changedBySec = {}, changedByGrp = {};
  Object.keys(FIELDS).forEach(function (k) {
    var row = document.querySelector('#inputPanel [data-row="' + k + '"]');
    if (!row) return;
    var changed = !isRef && inp[k] !== ref[k] && !customInactive(FIELDS[k], inp);
    row.classList.toggle("isChanged", changed);
    /* Bestand/Reuse, der tatsächlich etwas einspart, hellgrün hinterlegen */
    row.classList.toggle("isReuse", /Reuse$/.test(k) && inp[k] > 0);
    row.title = changed ? refVariant().name + ": " + fieldValueText(FIELDS[k], ref[k]) : "";
    if (changed) {
      changedBySec[FIELDS[k].section.id] = (changedBySec[FIELDS[k].section.id] || 0) + 1;
      var comp = FIELDS[k].group.comp;
      if (comp) changedByGrp[comp] = (changedByGrp[comp] || 0) + 1;
    }
  });

  document.querySelectorAll("#inputPanel .grpHead").forEach(function (head) {
    var comp = head.querySelector(".grpTotal").getAttribute("data-grp");
    var n = changedByGrp[comp] || 0;
    head.querySelector(".secChanged").textContent = n ? n + " geändert" : "";
    head.querySelector(".grpTotal").textContent = fmt(res.components[comp].kg / 1000, 1) + " t CO₂-eq";
  });

  SECTIONS.forEach(function (s) {
    var n = changedBySec[s.id] || 0;
    document.querySelector('.secChanged[data-sec="' + s.id + '"]').textContent = n ? n + " geändert" : "";
    var comps = [];
    s.groups.forEach(function (g) { if (g.comp) comps.push(g.comp); });
    var tot = comps.reduce(function (a, k) { return a + res.components[k].kg; }, 0);
    /* Ohne jede Eingabe (auch vor Abzug Bestand/Reuse) ist der Abschnitt vermutlich vergessen gegangen */
    var empty = comps.length && !comps.some(function (k) { return res.components[k].gross; });
    var node = document.querySelector('.secTotal[data-sec="' + s.id + '"]');
    node.textContent = comps.length ? fmt(tot / 1000, 1) + " t CO₂-eq" : "";
    node.classList.toggle("isEmpty", !!empty);
    node.title = empty ? "Keine Eingabe in diesem Abschnitt" : "";
  });

  renderBuilding();
}

/* ---------------------------------------------------------------------
   6. Abmessungen – isometrische Darstellung der Allgemeinen Angaben
   --------------------------------------------------------------------- */
function pos(v) {
  v = parseFloat(v);
  return isFinite(v) && v > 0 ? v : 0;
}

/* Isometrie: x (Länge) nach rechts unten, y (Breite) nach links unten, z nach oben */
var ISO_C = Math.cos(Math.PI / 6);
function iso(p) { return [(p[0] - p[1]) * ISO_C, (p[0] + p[1]) / 2 - p[2]]; }

/* Quader von (x0, y0, z0) bis (x1, y1, z1): sichtbar sind Deckfläche sowie
   die vordere linke (y = y1) und vordere rechte (x = x1) Seite */
function isoBox(x0, y0, z0, x1, y1, z1, cls) {
  return [
    { cls: cls + "L", pts: [[x0, y1, z0], [x1, y1, z0], [x1, y1, z1], [x0, y1, z1]] },
    { cls: cls + "R", pts: [[x1, y1, z0], [x1, y0, z0], [x1, y0, z1], [x1, y1, z1]] },
    { cls: cls + "Top", pts: [[x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1]] }
  ];
}

/* Teil eines Quaders unterhalb der Terrainoberkante zt, sehr transparent in der
   Farbe des Erdreichs überlagert, damit er als eingebunden erkennbar ist */
function isoEarth(x0, y0, z0, x1, y1, z1, zt) {
  if (zt <= z0) return [];
  return isoBox(x0, y0, z0, x1, y1, Math.min(z1, zt), "isoEarth").filter(function (f) {
    return f.cls !== "isoEarthTop" || z1 <= zt;
  });
}

/* Geschosslinien auf den beiden sichtbaren Seiten */
function isoStoreys(l, b, z0, sh, n) {
  var out = [];
  for (var i = 1; i < n; i++) {
    var z = z0 + i * sh;
    out.push({ cls: "isoFloor", line: true, pts: [[-l / 2, b / 2, z], [l / 2, b / 2, z], [l / 2, -b / 2, z]] });
  }
  return out;
}

/* Masslinie entlang einer Kante: Hilfslinien von a/b nach aussen (Richtung off),
   Masslinie dazwischen, Text parallel zur Kante (angle in Grad, side ±1) */
function isoDim(a, b, off, label, angle, side, cls) {
  function add(p, f) { return [p[0] + off[0] * f, p[1] + off[1] * f, p[2] + off[2] * f]; }
  return [
    { cls: "isoDim", line: true, pts: [a, add(a, 1.3)] },
    { cls: "isoDim", line: true, pts: [b, add(b, 1.3)] },
    { cls: "isoDim", line: true, pts: [add(a, 1), add(b, 1)] },
    { text: label, at: add([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], 1), angle: angle, side: side, cls: cls }
  ];
}

function renderBuilding() {
  var x = activeVariant().inputs;
  var og = { l: pos(x.ogL), b: pos(x.ogB), sh: pos(x.ogH), n: Math.round(pos(x.ogN)) };
  var ug = { l: pos(x.ugL), b: pos(x.ugB), sh: pos(x.ugH), n: Math.round(pos(x.ugN)) };
  og.h = og.sh * og.n;
  ug.h = ug.sh * ug.n;
  var hasOg = og.l && og.b && og.h, hasUg = ug.l && ug.b && ug.h;
  /* Versatz: um so viel ragen die Untergeschosse über das Terrain hinaus (negativ:
     Terrain über der Decke über UG) */
  var off = parseFloat(x.ugOffset);
  ug.off = hasUg && isFinite(off) ? Math.max(-og.h, Math.min(off, ug.h)) : 0;
  /* Ohne Untergeschoss bestimmt allein das Obergeschoss den Ausschnitt */
  var fp = hasUg ? ug : { l: 0, b: 0, h: 0 };
  var big = Math.max(og.l, og.b, fp.l, fp.b, og.h + fp.h, 1);

  /* Terrain als Erdblock, vorne bündig mit dem Gebäude aufgeschnitten,
     damit die Untergeschosse sichtbar bleiben; Oberkante um den Versatz tiefer */
  var m = 0.1 * big, t = 0.025 * big, o = 0.07 * big;
  var cx = Math.max(og.l, fp.l) / 2, cy = Math.max(og.b, fp.b) / 2;
  var items = isoBox(-cx - m, -cy - m, -(fp.h + t), cx, cy, -ug.off, "isoPlate");
  if (hasUg) {
    items = items.concat(isoBox(-ug.l / 2, -ug.b / 2, -ug.h, ug.l / 2, ug.b / 2, 0, "isoUg"));
    items = items.concat(isoStoreys(ug.l, ug.b, -ug.h, ug.sh, ug.n));
    items = items.concat(isoEarth(-ug.l / 2, -ug.b / 2, -ug.h, ug.l / 2, ug.b / 2, 0, -ug.off));
  }
  if (hasOg) {
    items = items.concat(isoBox(-og.l / 2, -og.b / 2, 0, og.l / 2, og.b / 2, og.h, "isoOg"));
    items = items.concat(isoStoreys(og.l, og.b, 0, og.sh, og.n));
    items = items.concat(isoEarth(-og.l / 2, -og.b / 2, 0, og.l / 2, og.b / 2, og.h, -ug.off));
    /* Länge und Breite an den hinteren Dachkanten */
    items = items.concat(
      isoDim([-og.l / 2, -og.b / 2, og.h], [og.l / 2, -og.b / 2, og.h], [0, -o, 0], fmtAuto(og.l) + " m", 30, -1, "isoLbl"),
      isoDim([-og.l / 2, og.b / 2, og.h], [-og.l / 2, -og.b / 2, og.h], [-o, 0, 0], fmtAuto(og.b) + " m", -30, -1, "isoLbl"));
  }
  if (hasUg) {
    /* Untergeschoss: Länge und Breite an den vorderen unteren Kanten */
    items = items.concat(
      isoDim([-ug.l / 2, ug.b / 2, -ug.h], [ug.l / 2, ug.b / 2, -ug.h], [0, o, 0], fmtAuto(ug.l) + " m", 30, 1, "isoLbl isoLblUg"),
      isoDim([ug.l / 2, ug.b / 2, -ug.h], [ug.l / 2, -ug.b / 2, -ug.h], [o, 0, 0], fmtAuto(ug.b) + " m", -30, 1, "isoLbl isoLblUg"));
  }

  /* Höhen rechts der Zeichnung, in Bildschirmkoordinaten */
  var heights = [];
  if (hasOg) heights.push({ a: [og.l / 2, -og.b / 2, 0], b: [og.l / 2, -og.b / 2, og.h], lines: [fmtAuto(og.h) + " m", og.n + " × " + fmtAuto(og.sh) + " m"] });
  if (hasUg) heights.push({ a: [ug.l / 2, -ug.b / 2, -ug.h], b: [ug.l / 2, -ug.b / 2, 0], lines: [fmtAuto(ug.h) + " m", ug.n + " × " + fmtAuto(ug.sh) + " m"].concat(ug.off ? ["Versatz " + (ug.off > 0 ? "+" : "−") + fmtAuto(Math.abs(ug.off)) + " m"] : []), ug: true });

  /* Einpassen in die Zeichenfläche */
  var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  items.forEach(function (it) {
    (it.pts || [it.at]).forEach(function (p) {
      var q = iso(p);
      minX = Math.min(minX, q[0]); maxX = Math.max(maxX, q[0]);
      minY = Math.min(minY, q[1]); maxY = Math.max(maxY, q[1]);
    });
  });
  var W = 480, pad = { l: 34, r: 92, t: 22, b: 26 };
  var sc = Math.min((W - pad.l - pad.r) / (maxX - minX), (380 - pad.t - pad.b) / (maxY - minY));
  var H = Math.round((maxY - minY) * sc + pad.t + pad.b);
  var dx = pad.l + ((W - pad.l - pad.r) - (maxX - minX) * sc) / 2 - minX * sc, dy = pad.t - minY * sc;
  function P(p) { var q = iso(p); return [q[0] * sc + dx, q[1] * sc + dy]; }
  function pts(list) { return list.map(function (p) { var q = P(p); return q[0].toFixed(1) + "," + q[1].toFixed(1); }).join(" "); }

  /* Geschosslinien nur, solange sie sich noch unterscheiden lassen */
  var floors = Math.min(og.sh || Infinity, ug.sh || Infinity) * sc >= 4;

  var body = items.map(function (it) {
    if (it.cls === "isoFloor" && !floors) return "";
    if (it.text) {
      var q = P(it.at);
      return '<text class="' + it.cls + '" text-anchor="middle" transform="translate(' + q[0].toFixed(1) + "," + q[1].toFixed(1) +
        ") rotate(" + it.angle + ')" dy="' + (it.side < 0 ? -4 : 11) + '">' + esc(it.text) + "</text>";
    }
    return it.line
      ? '<polyline class="' + it.cls + '" points="' + pts(it.pts) + '"/>'
      : '<polygon class="' + it.cls + '" points="' + pts(it.pts) + '"/>';
  }).join("");

  var xr = maxX * sc + dx + 10;
  heights.forEach(function (h) {
    var y1 = P(h.b)[1], y2 = P(h.a)[1], xc = P(h.a)[0];
    body += '<polyline class="isoDim" points="' + xc.toFixed(1) + "," + y1.toFixed(1) + " " + (xr + 3).toFixed(1) + "," + y1.toFixed(1) + '"/>' +
      '<polyline class="isoDim" points="' + xc.toFixed(1) + "," + y2.toFixed(1) + " " + (xr + 3).toFixed(1) + "," + y2.toFixed(1) + '"/>' +
      '<polyline class="isoDim" points="' + xr.toFixed(1) + "," + y1.toFixed(1) + " " + xr.toFixed(1) + "," + y2.toFixed(1) + '"/>';
    var mid = (y1 + y2) / 2;
    body += '<text class="isoLbl' + (h.ug ? " isoLblUg" : "") + '" x="' + (xr + 6).toFixed(1) + '" y="' + (mid - 1).toFixed(1) + '">' + esc(h.lines[0]) + "</text>" +
      h.lines.slice(1).map(function (l, i) {
        return '<text class="isoLbl isoMuted" x="' + (xr + 6).toFixed(1) + '" y="' + (mid + 11 + 12 * i).toFixed(1) + '">' + esc(l) + "</text>";
      }).join("");
  });

  el("bldViz").innerHTML =
    '<svg class="isoSvg" viewBox="0 0 ' + W + " " + H + '" role="img" aria-label="Gebäude in isometrischer Ansicht">' + body + "</svg>" +
    '<div class="vzLegend"><span><i class="sw vzSwOg"></i>Obergeschosse</span><span><i class="sw vzSwUg"></i>Untergeschosse</span><span><i class="sw vzSwEarth"></i>Erdreich</span></div>';
}

/* ---------------------------------------------------------------------
   7. Variantenliste
   --------------------------------------------------------------------- */
function renderVariantList() {
  var full = shownCount() >= MAX_SHOWN;
  el("variantList").innerHTML = state.variants.map(function (v, i) {
    var isRef = i === state.ref;
    var lock = isRef || (!v.shown && full);
    var why = isRef ? "Die Referenz ist immer im Vergleich" : !v.shown && full ? "Höchstens " + MAX_SHOWN + " Varianten im Vergleich" : "";
    return '<div class="variantPill' + (i === state.active ? " active" : "") + (v.shown ? "" : " isHidden") + '">' +
      '<button type="button" role="tab" class="vpMain" data-i="' + i + '" aria-selected="' + (i === state.active) + '">' +
      '<span class="vpName">' + esc(v.name) + (isRef ? ' <span class="vpRef">Referenz</span>' : "") + "</span>" +
      '<span class="vpVal">' + fmt(results[i].total / 1000, 1) + " t CO₂-eq</span></button>" +
      '<label class="vpShow"' + (why ? ' title="' + why + '"' : "") + '><input type="checkbox" data-show="' + i + '"' +
      (v.shown ? " checked" : "") + (lock ? " disabled" : "") + "> im Vergleich</label></div>";
  }).join("");

  el("btnAdd").disabled = state.variants.length >= MAX_VARIANTS;
  el("btnDelete").disabled = state.variants.length <= 1;
  el("btnRef").disabled = state.active === state.ref;
  el("variantNote").textContent = state.variants.length > 1
    ? "Eingaben, die von der Referenz abweichen, sind markiert. Diagramm und Tabellen zeigen die Referenz und bis zu " +
      (MAX_SHOWN - 1) + " weitere Varianten, die «im Vergleich» angewählt sind."
    : "Mit «+ Variante (Kopie)» wird die aktive Variante kopiert. So lassen sich einzelne Eingaben ändern und die Auswirkungen vergleichen.";
}

/* Spalten der Auswertung: Referenz zuerst, dann die übrigen angewählten Varianten */
function columns() {
  var order = [state.ref];
  state.variants.forEach(function (v, i) { if (v.shown && i !== state.ref) order.push(i); });
  return order.map(function (i) {
    var v = state.variants[i];
    return { i: i, name: v.name, inputs: v.inputs, res: results[i], active: i === state.active };
  });
}

/* ---------------------------------------------------------------------
   8. Gliederung und Bezugsgrösse der Ausgabe
   --------------------------------------------------------------------- */
function groupItems() {
  if (state.grouping === "components") {
    return STACK_ORDER.map(function (k) {
      var c = M.COMPONENTS.filter(function (x) { return x.key === k; })[0];
      var hg = M.MAIN_GROUPS.filter(function (g) { return g.key === c.hg; })[0];
      return {
        key: k, name: c.name, hg: c.hg, kat: c.kat, group: hg.name, color: COLORS.components[k],
        get: function (r) { return r.components[k].kg; }
      };
    });
  }
  var list = state.grouping === "mainGroups" ? M.MAIN_GROUPS.slice().reverse() : M.CATEGORIES.slice().reverse();
  return list.map(function (g) {
    return { key: g.key, name: g.name, color: COLORS[state.grouping][g.key], get: function (r) { return r[state.grouping][g.key]; } };
  });
}

/* ---------------------------------------------------------------------
   9. Gestapeltes Säulendiagramm (eine Säule je Variante), bezogen auf
      m² EBF und Jahr – vergleichbar mit dem Zielwert
   --------------------------------------------------------------------- */
function perEbfYear(res, kg) {
  var d = res.eval.ebf * res.eval.lifespan;
  return d ? kg / d : NaN;
}

/* print: Diagramm in fester Breite für den Druck, ohne Spaltenraster */
function plotStack(cols, items, print) {
  if (!print && !paneOpen("output")) return;
  var xs = cols.map(function (_, i) { return i; });
  var ticks = cols.map(function (c) { return c.name; });

  var traces = items.map(function (it, idx) {
    var ys = cols.map(function (c) { return perEbfYear(c.res, it.get(c.res)); });
    var share = cols.map(function (c) { return c.res.total ? it.get(c.res) / c.res.total * 100 : 0; });
    return {
      x: xs, y: ys, name: it.name, type: "bar",
      legendgroup: it.group,
      /* Legende in derselben Reihenfolge wie der Stapel: oberstes Bauteil zuerst */
      legendrank: items.length - idx,
      legendgrouptitle: it.group ? { text: esc(it.group), font: { color: INK.primary, size: 12 } } : undefined,
      customdata: cols.map(function (c, i) { return [c.name, share[i]]; }),
      marker: { color: it.color, line: { color: INK.surface, width: 1 } },
      hovertemplate: "<b>" + esc(it.name) + "</b><br>%{customdata[0]}: %{y:,.2f} kg CO₂-eq/m²a" +
        "<br>Anteil %{customdata[1]:.1f} %<extra></extra>"
    };
  });

  var totals = cols.map(function (c) { return c.res.eval.epf; });
  traces.push({
    x: xs, y: totals, type: "scatter", mode: "text",
    text: totals.map(function (v) { return fmt(v, 2); }),
    textposition: "top center", textfont: { color: INK.primary, size: 12 },
    showlegend: false, hoverinfo: "skip", cliponaxis: false
  });

  /* Zielwert je Säule als gestrichelte Marke; unsichtbare Punkte darauf
     liefern den Hover-Text und halten die Marke im Wertebereich */
  var tx = [], ty = [];
  cols.forEach(function (c, i) {
    if (!isFinite(c.res.eval.target)) return;
    tx.push(i - 0.42, i + 0.42, null);
    ty.push(c.res.eval.target, c.res.eval.target, null);
  });
  if (tx.length) {
    traces.push({
      x: tx, y: ty, type: "scatter", mode: "lines", name: "Zielwert", legendrank: items.length + 1,
      line: { color: INK.primary, width: 1.5, dash: "dash" },
      hovertemplate: "Zielwert %{y:,.2f} kg CO₂-eq/m²a<extra></extra>"
    });
  }

  /* Aktive Variante hinterlegen, sobald mehrere Säulen da sind */
  var shapes = [];
  cols.forEach(function (c, i) {
    if (c.active && cols.length > 1) {
      shapes.push({
        type: "rect", xref: "x", yref: "paper", x0: i - 0.5, x1: i + 0.5, y0: 0, y1: 1,
        fillcolor: "rgba(11,11,11,0.05)", line: { width: 0 }, layer: "below"
      });
    }
  });

  var narrow = window.innerWidth < 768;
  /* Im Spaltenraster der Tabellen: Legende in der Bezeichnungsspalte,
     jede Säule mittig über der Spalte ihrer Variante */
  var rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
  var labelPx = LABEL_W * rem, plotPx = cols.length * COL_W * rem;
  var aligned = !print && !narrow && el("plotStack").clientWidth >= labelPx + plotPx;
  var layout = {
    barmode: "stack",
    bargap: aligned ? 0.45 : cols.length > 3 ? 0.3 : 0.55,
    barcornerradius: 4,
    /* Breit: feste Legendenspalte rechts, damit die Diagrammfläche beim Wechsel
       der Gliederung gleich breit bleibt (Plotly passt den Rand sonst an die Legende an) */
    margin: aligned ? { l: labelPx, r: 0, t: 24, b: 40 } : { l: 62, r: narrow ? 16 : LEGEND_W, t: 24, b: 40 },
    width: aligned ? labelPx + plotPx : print ? 700 : undefined,
    /* Schmal: Legende unter dem Diagramm – die gruppierte Bauteil-Legende braucht mehr Höhe */
    height: narrow ? (state.grouping === "components" ? 860 : 560) : 440,
    paper_bgcolor: INK.surface,
    plot_bgcolor: INK.surface,
    font: { family: "inherit", size: 12, color: INK.secondary },
    xaxis: {
      tickmode: "array", tickvals: xs, ticktext: ticks.map(esc),
      zeroline: false, linecolor: INK.grid, range: [-0.5, cols.length - 0.5]
    },
    yaxis: {
      title: { text: "Treibhausgasemissionen [kg CO₂-eq/m²a]", font: { color: INK.secondary } },
      gridcolor: INK.grid, zeroline: false, linecolor: INK.grid, ticks: "outside", tickcolor: INK.grid,
      rangemode: "tozero", separatethousands: true
    },
    hovermode: "closest",
    legend: aligned
      ? { orientation: "v", x: -labelPx / plotPx, xanchor: "left", y: 1, traceorder: "grouped", tracegroupgap: 8, font: { color: INK.secondary } }
      : narrow
      ? { orientation: "h", x: 0, y: -0.08, yanchor: "top", traceorder: "grouped", tracegroupgap: 8, font: { color: INK.secondary } }
      : { orientation: "v", x: 1.01, y: 1, traceorder: "grouped", tracegroupgap: 8, font: { color: INK.secondary } },
    shapes: shapes,
    separators: ".’"
  };

  Plotly.react("plotStack", traces, layout, window.pltConfig || { displaylogo: false, responsive: true });
}

/* ---------------------------------------------------------------------
   9b. Plakativer Vergleich: Flüge Zürich – New York (retour), im Variantenvergleich
   --------------------------------------------------------------------- */
var PLANE = '<svg class="icoPlane" viewBox="0 0 24 24" aria-hidden="true"><use href="#icoPlane"/></svg>';

function flightsText(res) {
  return fmt(res.equivalents.flights, 0) + " Flüge";
}

/* ---------------------------------------------------------------------
   10. Tabellen
   --------------------------------------------------------------------- */
function swatch(color) {
  return '<i class="sw" style="background:' + color + '"></i>';
}

/* Gemeinsames Spaltenraster der Ausgabe (rem): Bezeichnung bzw. Legende des
   Diagramms links, je Variante eine Spalte gleicher Breite. So läuft jede Variante
   vom Diagramm bis zum Variantenvergleich als eine Spalte durch. */
var LABEL_W = 24, COL_W = 13;

function alignedCols(table, n) {
  table.classList.add("alignedTable");
  table.style.width = (LABEL_W + n * COL_W) + "rem";
  return '<colgroup><col class="cLabel" style="width:' + LABEL_W + 'rem">' +
    new Array(n + 1).join('<col class="cVar" style="width:' + COL_W + 'rem">') + "</colgroup>";
}

function colHead(c, i) {
  return '<th class="text-right' + (c.active ? " colActive" : "") + '">' + esc(c.name) +
    (i === 0 ? '<br><span class="vpRef">Referenz</span>' : "") + "</th>";
}

/* Sortierknopf im Tabellenkopf; key: Spalte, nach der sortiert wird ("" = Lage im Gebäude) */
function sortHead(label, key, cls) {
  var s = state.sort, on = key ? !!s && s.key === key : !s;
  var aria = key && on ? (s.dir < 0 ? "descending" : "ascending") : "none";
  var ico = key ? '<span class="sortIco">' + (on ? (s.dir < 0 ? "▼" : "▲") : "↕") + "</span>" : "";
  return '<th class="' + (cls || "") + '" aria-sort="' + aria + '"><button type="button" class="sortBtn' + (on && key ? " active" : "") +
    '" data-sort="' + key + '" title="' + (key ? "Nach Wert sortieren" : "Nach Lage im Gebäude sortieren") + '">' + label + ico + "</button></th>";
}

/* Zeilen in Stapelreihenfolge (oberstes Bauteil zuerst) oder nach dem Wert einer Spalte */
function sortedItems(items, cols) {
  var rows = items.slice().reverse(), s = state.sort;
  var col = s ? cols[parseInt(s.key.replace(/\D/g, ""), 10)] : null;
  if (!col) return rows;
  return rows.sort(function (a, b) { return s.dir * (a.get(col.res) - b.get(col.res)); });
}

/* Eine Variante: Bauteil, t CO₂-eq und Anteil; sortierbar nach Wert */
function renderSingleTable(c, items) {
  var head = "<thead><tr>" + sortHead(state.grouping === "components" ? "Bauteil" : "Gruppe", "") +
    sortHead("t CO₂-eq", "t0", "text-right") + sortHead("Prozent", "p0", "text-right") + "</tr></thead>";
  var body = sortedItems(items, [c]).map(function (it) {
    var kg = it.get(c.res);
    return "<tr><td>" + swatch(it.color) + esc(it.name) + "</td>" +
      '<td class="num">' + fmt(kg / 1000, 1) + "</td>" +
      '<td class="num">' + fmt(c.res.total ? kg / c.res.total * 100 : NaN, 1) + " %</td></tr>";
  }).join("");
  var foot = '<tr class="rowTotal"><td>Gesamt</td><td class="num">' + fmt(c.res.total / 1000, 1) +
    '</td><td class="num">100.0 %</td></tr>';
  el("compareTable").innerHTML = alignedCols(el("compareTable"), 2) + head + "<tbody>" + body + foot + "</tbody>";
}

/* Mehrere Varianten: Werte mit Anteil am Total (zurückhaltend) */
function compareCell(v, total) {
  /* ohne Anteil (Summenzeile) bleibt die Spalte frei, damit die Werte bündig stehen */
  var pct = total === undefined ? "" : fmt(total ? v / total * 100 : NaN, 1) + " %";
  return fmt(v, 1) + '<span class="sharePct">' + pct + "</span>";
}

function renderCompareTable(cols, items) {
  var head = "<thead><tr>" + sortHead((state.grouping === "components" ? "Bauteil" : "Gruppe") +
    ' <span class="thUnit">[t CO₂-eq · Anteil]</span>', "") + cols.map(function (c, i) {
    return sortHead(esc(c.name) + (i === 0 ? '<br><span class="vpRef">Referenz</span>' : ""), "c" + i,
      "text-right" + (c.active ? " colActive" : ""));
  }).join("") + "</tr></thead>";

  /* withShare: Anteil am Total der Variante neben dem Wert (nicht in der Summenzeile) */
  function cells(get, withShare) {
    return cols.map(function (c) {
      var txt = compareCell(get(c), withShare ? c.res.total / 1000 : undefined);
      return '<td class="num' + (c.active ? " colActive" : "") + '">' + txt + "</td>";
    }).join("");
  }

  var body = sortedItems(items, cols).map(function (it) {
    return "<tr><td>" + swatch(it.color) + esc(it.name) + "</td>" +
      cells(function (c) { return it.get(c.res) / 1000; }, true) + "</tr>";
  }).join("");
  var foot = '<tr class="rowTotal"><td>Gesamt</td>' + cells(function (c) { return c.res.total / 1000; }) + "</tr>";
  var totals = cols.map(function (c) { return c.res.total / 1000; });
  foot += '<tr class="rowDelta"><td>Differenz zu ' + esc(cols[0].name) + "</td>" + totals.map(function (v, i) {
    var pad = '<span class="sharePct"></span>';
    if (i === 0) return '<td class="num">–' + pad + "</td>";
    var d = v - totals[0], p = totals[0] ? d / totals[0] * 100 : NaN;
    return '<td class="num">' + fmtSigned(d, 1) + pad + '<br><span class="deltaPct">' + fmtSigned(p, 1) + " %</span>" + pad + "</td>";
  }).join("") + "</tr>";
  el("compareTable").innerHTML = alignedCols(el("compareTable"), cols.length) + head + "<tbody>" + body + foot + "</tbody>";
}

var EVAL_ROWS = [
  { l: "Gesamt pro Geschoss über Terrain", u: "kg CO₂-eq", dec: 0, get: function (r) { return r.eval.perStoreyAbove; } },
  { l: "Gesamt pro Geschoss", u: "kg CO₂-eq", dec: 0, get: function (r) { return r.eval.perStorey; } },
  { l: "Fläche über Terrain", u: "m²", dec: 0, get: function (r) { return r.eval.areaAbove; } },
  { l: "GWP pro Fläche", u: "kg CO₂-eq/m²", dec: 1, get: function (r) { return r.eval.perArea; } },
  { l: "Energiebezugsfläche AE / EBF", u: "m²", dec: 0, get: function (r) { return r.eval.ebf; } },
  { l: "Amortisationszeit", u: "Jahre", dec: 0, get: function (r) { return r.eval.lifespan; } },
  { l: "Treibhausgasemissionen pro EBF und Jahr", u: "kg CO₂-eq/m²a", dec: 2, get: function (r) { return r.eval.epf; }, strong: true },
  { l: "Zielwert", u: "kg CO₂-eq/m²a", dec: 2, get: function (r) { return r.eval.target; } },
  { l: "Eingespart durch Bestand/Reuse", u: "kg CO₂-eq", dec: 0, get: function (r) { return r.eval.reuseKg; }, reuse: true }
];

function targetBadge(r, short) {
  if (!isFinite(r.eval.epf) || !isFinite(r.eval.target)) return "–";
  return r.eval.epf <= r.eval.target
    ? '<span class="status statusGood">✓ ' + (short ? "ja" : "eingehalten") + "</span>"
    : '<span class="status statusBad">✕ ' + (short ? "nein" : "überschritten") + "</span>";
}

function renderEvalTable(cols) {
  var multi = cols.length > 1;
  var head = "<thead><tr><th></th>" + (multi ? cols.map(colHead).join("") : '<th class="text-right">Wert</th>') + "</tr></thead>";
  var body = EVAL_ROWS.map(function (row) {
    return "<tr" + (row.strong ? ' class="rowTotal"' : "") + "><td>" + esc(row.l) +
      ' <span class="thUnit">[' + row.u + "]</span></td>" + cols.map(function (c) {
      var cls = "num" + (c.active && multi ? " colActive" : "") + (row.reuse && row.get(c.res) > 0 ? " isReuse" : "");
      return '<td class="' + cls + '">' + fmt(row.get(c.res), row.dec) + "</td>";
    }).join("") + "</tr>";
  }).join("");
  body += "<tr><td>Zielwert eingehalten</td>" + cols.map(function (c) {
    return '<td class="num' + (c.active && multi ? " colActive" : "") + '">' + targetBadge(c.res, multi) + "</td>";
  }).join("") + "</tr>";
  el("evalTable").innerHTML = alignedCols(el("evalTable"), cols.length) + head + "<tbody>" + body + "</tbody>";
}

/* Kosten: Kostenansätze (für alle Varianten) und Kosten je Bauteil */
function costName(key) {
  if (key === "slab") return "Decken aus Deckenrechner";
  return M.COST_ITEMS.filter(function (it) { return it.key === key; })[0].name;
}

function priceText(it, v) {
  return v === null ? "–" : fmtAuto(v) + " CHF/" + it.unit;
}

/* Eingabefelder einmal aufbauen, damit der Fokus beim Tippen erhalten bleibt */
function buildPriceTable() {
  el("priceTable").innerHTML = '<thead><tr><th>Position</th><th class="text-right">Ansatz</th><th></th>' +
    '<th class="text-right">Menge <span id="priceQtyName"></span></th></tr></thead><tbody>' +
    M.COST_ITEMS.map(function (it) {
      return '<tr data-price="' + it.key + '"><td>' + esc(it.name) + '<div class="priceBasis">' + esc(it.basis) + "</div></td>" +
        '<td class="priceCtl"><input type="number" step="any" min="0" class="form-control form-control-sm" data-price="' + it.key +
        '" aria-label="Kostenansatz ' + esc(it.name) + '" placeholder="–"></td>' +
        '<td class="unit">CHF/' + it.unit + '</td><td class="num" data-qty="' + it.key + '"></td></tr>';
    }).join("") + "</tbody>";
}

function fillPrices() {
  document.querySelectorAll("#priceTable input[data-price]").forEach(function (node) {
    var v = state.prices[node.getAttribute("data-price")];
    node.value = v === null ? "" : v;
  });
}

function renderCosts(cols) {
  var res = results[state.active];
  el("priceQtyName").textContent = activeVariant().name;
  M.COST_ITEMS.forEach(function (it) {
    var b = res.costs.byItem[it.key], v = state.prices[it.key];
    el("priceTable").querySelector('[data-qty="' + it.key + '"]').textContent = b ? fmt(b.qty, 0) + " " + it.unit : "–";
    var row = el("priceTable").querySelector('tr[data-price="' + it.key + '"]');
    var changed = v !== it.def;
    row.classList.toggle("isChanged", changed);
    row.title = changed ? "Standardwert: " + priceText(it, it.def) : "";
  });

  var multi = cols.length > 1;
  var keys = STACK_ORDER.slice().reverse().filter(function (k) {
    return cols.some(function (c) { return c.res.components[k].cost.lines.length; });
  });
  function cell(c, cost) {
    var cls = "num" + (c.active && multi ? " colActive" : "");
    if (!cost.lines.length) return '<td class="' + cls + '">–</td>';
    if (!cost.missing.length) return '<td class="' + cls + '">' + fmt(cost.chf, 0) + "</td>";
    var title = "Ohne Kostenansatz: " + cost.missing.map(costName).join(", ");
    return '<td class="' + cls + '"><span class="costMissing" title="' + esc(title) + '">' +
      (cost.chf ? fmt(cost.chf, 0) + "*" : "nicht erfasst") + "</span></td>";
  }
  var head = '<thead><tr><th>Bauteil <span class="thUnit">[CHF]</span></th>' +
    (multi ? cols.map(colHead).join("") : '<th class="text-right">Kosten</th>') + "</tr></thead>";
  var body = keys.map(function (k) {
    return "<tr><td>" + swatch(COLORS.components[k]) + esc(compName(k)) + "</td>" +
      cols.map(function (c) { return cell(c, c.res.components[k].cost); }).join("") + "</tr>";
  }).join("");
  body += '<tr class="rowTotal"><td>Gesamt</td>' + cols.map(function (c) {
    return cell(c, { lines: [1], chf: c.res.costs.total, missing: c.res.costs.missing });
  }).join("") + "</tr>";
  if (multi) {
    body += '<tr class="rowDelta"><td>Differenz zu ' + esc(cols[0].name) + "</td>" + cols.map(function (c, i) {
      var d = c.res.costs.total - cols[0].res.costs.total, t = cols[0].res.costs.total;
      return '<td class="num">' + (i === 0 ? "–" : fmtSigned(d, 0) + '<br><span class="deltaPct">' + fmtSigned(t ? d / t * 100 : NaN, 1) + " %</span>") + "</td>";
    }).join("") + "</tr>";
  }
  el("costTable").innerHTML = alignedCols(el("costTable"), cols.length) + head + "<tbody>" + body + "</tbody>";

  var missing = [];
  cols.forEach(function (c) {
    c.res.costs.missing.forEach(function (k) { if (missing.indexOf(k) === -1) missing.push(k); });
  });
  var where = missing.some(function (k) { return k !== "slab"; }) ? "unter «Kostenansätze bearbeiten»" : "";
  if (missing.indexOf("slab") !== -1) where += (where ? ", " : "") + "für die Decken je Variante unter «Decken und Dach»";
  el("costNote").innerHTML = (missing.length
    ? '<span class="costMissing">* Ohne Kostenansatz und daher nicht im Total: ' + esc(missing.map(costName).join(", ")) +
      ".</span> Ansätze " + where + ". "
    : "") + "Kosten = Menge × Kostenansatz; der Anteil Bestand/Reuse ist abgezogen.";
  el("costPrint").textContent = "Kostenansätze: " + M.COST_ITEMS.map(function (it) {
    return it.name + " " + priceText(it, state.prices[it.key]);
  }).join(" · ");
}

/* ---------------------------------------------------------------------
   11. Variantenvergleich – Varianten als Spalten nebeneinander, Eingaben
       und Resultate als Zeilen, gegliedert wie die Eingabemaske.
       Optional nur Zeilen, in denen sich die Varianten unterscheiden.
   --------------------------------------------------------------------- */
function compName(k) {
  return M.COMPONENTS.filter(function (x) { return x.key === k; })[0].name;
}

/* Zeilen der Vergleichstabelle: Überschriften (Ebene 0–2) und Wertezeilen */
function specRows() {
  var rows = [];
  SECTIONS.forEach(function (s) {
    rows.push({ level: 0, title: s.title });
    s.groups.forEach(function (g) {
      if (g.title && g.title !== s.title) rows.push({ level: 1, title: g.title });
      g.fields.forEach(function (f) {
        if (f.type === "heading") rows.push({ level: 2, title: f.l });
        if (!f.k) return;
        rows.push({
          hide: f.customFor ? function (cols) {
            return cols.every(function (c) { return customInactive(f, c.inputs); });
          } : null,
          label: f.l, field: f.k,
          text: function (c) { return customInactive(f, c.inputs) ? "–" : esc(fieldValueText(f, c.inputs[f.k])); },
          key: function (c) { return customInactive(f, c.inputs) ? null : c.inputs[f.k]; }
        });
      });
      if (g.comp) {
        rows.push({
          label: "GWP " + compName(g.comp), result: true,
          text: function (c) { return fmt(c.res.components[g.comp].kg / 1000, 1) + " t CO₂-eq"; },
          key: function (c) { return fmt(c.res.components[g.comp].kg / 1000, 1); }
        });
      }
    });
  });
  return rows;
}

/* Kennwerte ganz oben – immer sichtbar */
var SPEC_SUMMARY = [
  { l: "Treibhausgasemissionen total", text: function (c) { return fmt(c.res.total / 1000, 1) + " t CO₂-eq"; } },
  { l: "pro m² Geschossfläche über Terrain", text: function (c) { return fmt(c.res.eval.perArea, 1) + " kg CO₂-eq/m²"; } },
  { l: "pro m² EBF und Jahr", text: function (c) { return fmt(c.res.eval.epf, 2) + " kg CO₂-eq/m²a"; } },
  { l: "Zielwert eingehalten", text: function (c) { return targetBadge(c.res); } },
  { l: "Kosten total", text: function (c) {
    return fmt(c.res.costs.total, 0) + " CHF" + (c.res.costs.missing.length ? ' <span class="costMissing">unvollständig</span>' : "");
  } },
  { l: "Flüge Zürich – New York (retour)", text: function (c) { return PLANE + " " + flightsText(c.res); } }
];

/* onlyDiff und node lassen sich für den Druck überschreiben */
function renderSpecTable(cols, opt) {
  opt = opt || {};
  var onlyDiff = opt.hasOwnProperty("onlyDiff") ? opt.onlyDiff : state.onlyDiff;
  var ref = cols[0];
  var n = cols.length + 1;

  var head = '<thead><tr><th class="specLabel"></th>' + cols.map(function (c, i) {
    var d = c.res.total - ref.res.total, p = ref.res.total ? d / ref.res.total * 100 : NaN;
    var delta = i === 0
      ? '<span class="vpRef">Referenz</span>'
      : '<span class="specDelta' + (d > 0 ? " up" : d < 0 ? " down" : "") + '">' +
        fmtSigned(d / 1000, 1) + " t (" + fmtSigned(p, 1) + " %)</span>";
    return '<th class="specCol' + (c.active ? " colActive" : "") + '">' +
      '<button type="button" class="specPick" data-i="' + c.i + '" title="Variante bearbeiten">' + esc(c.name) + "</button>" +
      '<div class="specTotal">' + fmt(c.res.total / 1000, 1) + ' <span class="heroUnit">t CO₂-eq</span></div>' +
      delta + "</th>";
  }).join("") + "</tr></thead>";

  var body = '<tr class="specH0"><td colspan="' + n + '">Ergebnis</td></tr>' +
    SPEC_SUMMARY.map(function (row) {
      return '<tr><td class="specLabel">' + esc(row.l) + "</td>" + cols.map(function (c) {
        return '<td class="' + (c.active ? "colActive" : "") + '">' + row.text(c) + "</td>";
      }).join("") + "</tr>";
    }).join("");

  /* Sichtbarkeit von hinten her bestimmen: eine Überschrift erscheint nur,
     wenn darunter (bis zur nächsten gleich- oder höherrangigen) eine Zeile steht */
  var rows = specRows();
  var seen = [false, false, false], anyDiff = false;
  for (var r = rows.length - 1; r >= 0; r--) {
    var row = rows[r];
    if (row.level === undefined && row.hide && row.hide(cols)) {
      row.show = false;
    } else if (row.level === undefined) {
      var keys = cols.map(row.key);
      row.diff = keys.some(function (k) { return k !== keys[0]; });
      row.show = !onlyDiff || row.diff;
      if (row.diff) anyDiff = true;
      /* Bauteilresultat gehört zur Gruppe, nicht zur letzten Zwischenüberschrift */
      if (row.show) for (var d = 0; d < (row.result ? 2 : 3); d++) seen[d] = true;
    } else {
      row.show = seen[row.level];
      for (var j = row.level; j < 3; j++) seen[j] = false;
    }
  }

  body += rows.filter(function (row) { return row.show; }).map(function (row) {
    if (row.level !== undefined) {
      return '<tr class="specH' + row.level + '"><td colspan="' + n + '">' + esc(row.title) + "</td></tr>";
    }
    var refKey = row.key(ref);
    return '<tr class="' + (row.result ? "specResult" : "") + '"><td class="specLabel">' + esc(row.label) + "</td>" +
      cols.map(function (c, i) {
        var cls = [];
        if (c.active) cls.push("colActive");
        if (i > 0 && row.key(c) !== refKey) cls.push("isDiff");
        return '<td class="' + cls.join(" ") + '">' + row.text(c) + "</td>";
      }).join("") + "</tr>";
  }).join("");

  if (onlyDiff && !anyDiff) {
    body += '<tr><td colspan="' + n + '" class="specEmpty">Die Varianten unterscheiden sich in keiner Eingabe.</td></tr>';
  }

  var table = opt.node || el("specTable");
  table.innerHTML = (opt.node ? "" : alignedCols(table, cols.length)) + head + "<tbody>" + body + "</tbody>";
}

/* ---------------------------------------------------------------------
   11b. Export (PDF, Excel) und Zustand als Datei speichern/öffnen
   --------------------------------------------------------------------- */
var XLSX_URL = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";

function today() {
  var d = new Date();
  return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
}

function download(blob, name) {
  var a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 0);
}

/* Alle Varianten, Referenz zuerst – für Excel und Datei, unabhängig von «im Vergleich» */
function allColumns() {
  var order = [state.ref];
  state.variants.forEach(function (v, i) { if (i !== state.ref) order.push(i); });
  return order.map(function (i) {
    var v = state.variants[i];
    return { i: i, name: v.name, inputs: v.inputs, res: results[i], active: i === state.active };
  });
}

/* PDF über den Druckdialog des Browsers («Als PDF sichern»). Der Druck zeigt
   Ausgabe und alle Eingaben der Varianten im Vergleich; die Eingabemaske entfällt. */
function preparePrint() {
  var cols = columns();
  el("printDate").textContent = new Date().toLocaleDateString(LOCALE);
  el("printVariants").textContent = cols.map(function (c) { return c.name; }).join(", ");
  renderSpecTable(cols, { onlyDiff: false, node: el("printInputs") });
  plotStack(cols, groupItems(), true);
}

/* afterprint kann noch im Drucklayout eintreffen; die Breite daher erst
   zurücksetzen, wenn wieder das Bildschirmlayout gilt, und dann neu einpassen */
function restoreAfterPrint() {
  setTimeout(function () {
    if (window.matchMedia("print").matches) return restoreAfterPrint();
    if (!paneOpen("output")) return;
    plotStack(columns(), groupItems());
    Plotly.Plots.resize(el("plotStack"));
  }, 100);
}

function exportExcel() {
  var cols = allColumns();
  var names = cols.map(function (c) { return c.name; });

  var summary = [["Kennwert", "Einheit"].concat(names)];
  [
    ["Treibhausgasemissionen total", "t CO₂-eq", function (r) { return r.total / 1000; }],
    ["pro m² Geschossfläche über Terrain", "kg CO₂-eq/m²", function (r) { return r.eval.perArea; }],
    ["pro m² EBF und Jahr", "kg CO₂-eq/m²a", function (r) { return r.eval.epf; }],
    ["Zielwert", "kg CO₂-eq/m²a", function (r) { return r.eval.target; }],
    ["Zielwert eingehalten", "", function (r) { return isFinite(r.eval.epf) ? (r.eval.epf <= r.eval.target ? "ja" : "nein") : ""; }],
    ["Flüge Zürich – New York (retour)", "Anzahl", function (r) { return r.equivalents.flights; }]
  ].forEach(function (row) {
    summary.push([row[0], row[1]].concat(cols.map(function (c) { return cellValue(row[2](c.res)); })));
  });
  summary.push([]);
  summary.push(["Bauteil", "Hauptgruppe", "Kategorie"].concat(names.map(function (n) { return n + " [kg CO₂-eq]"; })));
  M.COMPONENTS.forEach(function (comp) {
    summary.push([comp.name, comp.hg, comp.kat].concat(cols.map(function (c) { return c.res.components[comp.key].kg; })));
  });
  summary.push(["Gesamt", "", ""].concat(cols.map(function (c) { return c.res.total; })));

  var evalRows = [["Kennwert", "Einheit"].concat(names)];
  EVAL_ROWS.forEach(function (row) {
    evalRows.push([row.l, row.u].concat(cols.map(function (c) { return cellValue(row.get(c.res)); })));
  });

  var inputs = [["Abschnitt", "Gruppe", "Teil", "Eingabe", "Einheit"].concat(names)];
  SECTIONS.forEach(function (sec) {
    sec.groups.forEach(function (g) {
      var part = "";
      g.fields.forEach(function (f) {
        if (f.type === "heading") part = f.l;
        if (!f.k) return;
        if (cols.every(function (c) { return customInactive(f, c.inputs); })) return;
        inputs.push([sec.title, g.title || "", part, f.l, f.type === "num" ? f.u : ""].concat(cols.map(function (c) {
          if (customInactive(f, c.inputs)) return "";
          var v = c.inputs[f.k];
          return f.type === "bool" ? (v ? "ja" : "nein") : v;
        })));
      });
    });
  });

  var costs = [["Bauteil"].concat(names.map(function (n) { return n + " [CHF]"; }))];
  M.COMPONENTS.forEach(function (comp) {
    costs.push([comp.name].concat(cols.map(function (c) { return c.res.components[comp.key].cost.chf; })));
  });
  costs.push(["Gesamt"].concat(cols.map(function (c) { return c.res.costs.total; })));
  costs.push(["Ohne Kostenansatz (nicht im Total)"].concat(cols.map(function (c) { return c.res.costs.missing.map(costName).join(", "); })));
  costs.push([]);
  costs.push(["Kostenansatz (alle Varianten)", "Wert", "Einheit"]);
  M.COST_ITEMS.forEach(function (it) {
    var v = state.prices[it.key];
    costs.push([it.name, v === null ? "" : v, "CHF/" + it.unit]);
  });

  var wb = XLSX.utils.book_new();
  [["Ergebnis", summary, 34], ["Auswertung", evalRows, 40], ["Kosten", costs, 40], ["Eingaben", inputs, 22]].forEach(function (sh) {
    var ws = XLSX.utils.aoa_to_sheet(sh[1]);
    ws["!cols"] = sh[1][0].map(function (_, i) { return { wch: i === 0 ? sh[2] : 18 }; });
    XLSX.utils.book_append_sheet(wb, ws, sh[0]);
  });
  XLSX.writeFile(wb, "CO2-Rechner_" + today() + ".xlsx");
}

/* Zahl für Excel; nicht berechenbare Werte bleiben leer */
function cellValue(v) {
  return typeof v === "number" ? (isFinite(v) ? v : "") : v;
}

/* SheetJS erst bei Bedarf laden */
function withXlsx(fn) {
  if (window.XLSX) return fn();
  var sc = document.createElement("script");
  sc.src = XLSX_URL;
  sc.onload = fn;
  sc.onerror = function () { window.alert("Die Excel-Bibliothek konnte nicht geladen werden."); };
  document.head.appendChild(sc);
}

function saveFile() {
  var blob = new Blob([JSON.stringify(snapshot(), null, 1)], { type: "application/json" });
  download(blob, "CO2-Rechner_" + today() + ".json");
}

function openFile(file) {
  var reader = new FileReader();
  reader.onload = function () {
    var s;
    try { s = JSON.parse(reader.result); } catch (e) { s = null; }
    if (!s || s.app !== "co2rechner" || !Array.isArray(s.variants)) {
      window.alert("«" + file.name + "» ist keine gespeicherte Datei des CO₂-Rechners.");
      return;
    }
    if (!window.confirm("Die aktuellen Varianten durch die " + s.variants.length + " Variante(n) aus «" + file.name + "» ersetzen?")) return;
    var issues = [];
    if (!applySnapshot(s, issues)) {
      window.alert("«" + file.name + "» enthält keine gültigen Varianten.");
      return;
    }
    recompute();
    fillPrices();
    showOpenNotice(file.name, s, issues);
    selectVariant(state.active);
  };
  reader.readAsText(file);
}

/* Hinweis nach dem Öffnen: was nicht übernommen werden konnte und ob sich
   die Resultate seit dem Speichern geändert haben (neue Daten oder Berechnung) */
function issueText(it, inputs) {
  var f = FIELDS[it.k];
  var name = f ? f.l + (f.group.title ? " (" + f.group.title + ")" : "") : it.k;
  var now = f ? fieldValueText(f, inputs[it.k]) : "";
  if (it.kind === "renamed") return name + ": «" + it.was + "» heisst in den neuen Ökobilanzdaten «" + now + "»";
  if (it.kind === "unavailable") return name + ": «" + it.was + "» ist nicht mehr verfügbar, ersetzt durch «" + now + "»";
  if (it.kind === "invalid") return name + ": ungültiger Wert, ersetzt durch " + now;
  return name + ": neue Eingabe, Standardwert " + now;
}

function showOpenNotice(fileName, s, issues) {
  var items = [];
  if (s.kbob && s.kbob !== DATA.kbob) {
    items.push("Gespeichert mit Ökobilanzdaten «" + s.kbob + "», gerechnet wird jetzt mit «" + DATA.kbob + "».");
  }
  if (!s.prices) {
    items.push("Die Datei enthält noch keine Kostenansätze; es gelten die Standardwerte.");
  }
  if (s.variants.length > MAX_VARIANTS) {
    items.push("Die Datei enthält " + s.variants.length + " Varianten; übernommen wurden die ersten " + MAX_VARIANTS + ".");
  }
  state.variants.forEach(function (v, i) {
    var lines = (issues[i] || []).map(function (it) { return issueText(it, v.inputs); });
    var saved = s.variants[i].saved;
    if (saved && isFinite(saved.total) && Math.abs(saved.total - results[i].total) >= 50) {
      lines.push("Treibhausgasemissionen total beim Speichern " + fmt(saved.total / 1000, 1) + " t CO₂-eq, jetzt " +
        fmt(results[i].total / 1000, 1) + " t CO₂-eq");
    }
    if (lines.length) {
      items.push("<b>" + esc(v.name) + "</b><ul>" + lines.map(function (l) { return "<li>" + esc(l) + "</li>"; }).join("") + "</ul>");
    }
  });
  var node = el("openNotice");
  node.className = "alert alert-dismissible mt-2 mb-0 openNotice " + (items.length ? "alert-warning" : "alert-success");
  node.innerHTML = "<div>«" + esc(fileName) + "» geöffnet." + (items.length ? " Bitte prüfen:" : " Alle Eingaben wurden unverändert übernommen.") + "</div>" +
    items.map(function (t) { return '<div class="mt-1">' + t + "</div>"; }).join("") +
    '<button type="button" class="close" aria-label="Schliessen">&times;</button>';
  node.hidden = false;
}

/* ---------------------------------------------------------------------
   12. Ausgabe aktualisieren
   --------------------------------------------------------------------- */
function renderHero() {
  var r = results[state.active];
  el("heroTotal").textContent = fmt(r.total / 1000, 1);
  el("heroEpf").textContent = fmt(r.eval.epf, 2);
  el("heroTarget").innerHTML = targetBadge(r) + ' <span class="dbNote">Zielwert ' + fmt(r.eval.target, 2) + "</span>";
  el("outputName").textContent = activeVariant().name;
}

function renderOutputs() {
  renderHero();
  var cols = columns();
  var items = groupItems();
  var multi = cols.length > 1;
  /* Sortierung beim Wechsel zwischen einer und mehreren Varianten mitnehmen */
  if (state.sort && multi !== (state.sort.key.charAt(0) === "c")) {
    state.sort.key = multi ? "c0" : state.sort.key === "c0" ? "t0" : null;
    if (!state.sort.key) state.sort = null;
  }
  plotStack(cols, items);
  if (multi) renderCompareTable(cols, items);
  else renderSingleTable(cols[0], items);
  renderEvalTable(cols);
  renderCosts(cols);

  el("specBox").style.display = multi ? "" : "none";
  if (multi) renderSpecTable(cols);
  renderPaneMeta(results[state.active]);
}

/* ---------------------------------------------------------------------
   12b. Bereiche ausklappbar; im Druck erscheinen alle
   --------------------------------------------------------------------- */
var PANES = ["input", "output", "eval", "costs", "compare"];

function paneOpen(p) {
  return !state.collapsed[p];
}

function renderPanes() {
  document.querySelectorAll(".mainPane").forEach(function (node) {
    var p = node.getAttribute("data-pane"), open = paneOpen(p);
    node.classList.toggle("isCollapsed", !open);
    node.querySelector(".paneToggle").setAttribute("aria-expanded", open);
  });
}

function setPane(p, open) {
  if (open) delete state.collapsed[p];
  else state.collapsed[p] = true;
  renderPanes();
  $("#inputPanel [data-toggle=popover]").popover("hide");
  /* Das Diagramm wird nur aufgeklappt gezeichnet */
  if (p === "output" && open) plotStack(columns(), groupItems());
  save();
}

/* Kennwerte der aktiven Variante im Titel, sichtbar wenn zugeklappt */
function renderPaneMeta(r) {
  el("metaOutput").textContent = fmt(r.total / 1000, 1) + " t CO₂-eq";
  el("metaEval").textContent = fmt(r.eval.epf, 2) + " kg CO₂-eq/m²a";
  el("metaCosts").textContent = fmt(r.costs.total, 0) + " CHF" + (r.costs.missing.length ? "*" : "");
  el("metaCompare").textContent = shownCount() + " Varianten";
}

function recompute() {
  results = state.variants.map(function (v) {
    syncEbf(v.inputs);
    return M.calc(v.inputs, DATA, state.prices);
  });
}

var pending = false;
function refresh() {
  if (pending) return;
  pending = true;
  window.requestAnimationFrame(function () {
    pending = false;
    recompute();
    updateLookups();
    updateInputDecorations();
    renderVariantList();
    renderOutputs();
    syncToggles();
    save();
  });
}

function selectVariant(i) {
  state.active = i;
  fillInputs();
  refresh();
}

/* ---------------------------------------------------------------------
   13. Bedienung
   --------------------------------------------------------------------- */
function bindControls() {
  var panel = el("inputPanel");
  function onInput(e) {
    var k = e.target.getAttribute("data-k");
    if (!k) return;
    var f = FIELDS[k], v = e.target.value;
    if (f.type === "num") {
      v = parseFloat(String(v).replace(",", "."));
      if (!isFinite(v)) return;
    } else if (f.type === "bool") {
      v = v === "true";
    }
    var inp = activeVariant().inputs;
    if (k === "ebf") inp.ebfAuto = false;
    /* Beim Wechsel auf «Benutzerdefiniert» mit dem GWP der bisherigen Wahl beginnen */
    if (f.type === "sel" && v === M.CUSTOM && inp[k] !== M.CUSTOM && FIELDS[k + "Gwp"] && !inp[k + "Gwp"]) {
      inp[k + "Gwp"] = +listGwp(f.list, inp[k]).toPrecision(6);
      el("f_" + k + "Gwp").value = inp[k + "Gwp"];
    }
    inp[k] = v;
    refresh();
  }
  panel.addEventListener("input", onInput);
  panel.addEventListener("change", onInput);

  el("variantList").addEventListener("click", function (e) {
    var b = e.target.closest(".vpMain");
    if (b) selectVariant(Number(b.getAttribute("data-i")));
  });

  el("variantList").addEventListener("change", function (e) {
    var i = e.target.getAttribute("data-show");
    if (i === null) return;
    state.variants[Number(i)].shown = e.target.checked;
    normalizeShown();
    refresh();
  });

  el("specTable").addEventListener("click", function (e) {
    var b = e.target.closest(".specPick");
    if (!b) return;
    selectVariant(Number(b.getAttribute("data-i")));
    setPane("input", true);
    el("app").scrollIntoView({ behavior: "smooth" });
  });

  el("btnAdd").addEventListener("click", function () {
    if (state.variants.length >= MAX_VARIANTS) return;
    state.variants.push({ name: nextName(), inputs: clone(activeVariant().inputs), shown: shownCount() < MAX_SHOWN });
    selectVariant(state.variants.length - 1);
  });

  el("btnRename").addEventListener("click", function () {
    var n = window.prompt("Name der Variante", activeVariant().name);
    if (n === null) return;
    n = n.trim();
    if (n) { activeVariant().name = n.slice(0, 40); fillInputs(); refresh(); }
  });

  el("btnRef").addEventListener("click", function () {
    state.ref = state.active;
    normalizeShown();
    refresh();
  });

  el("btnDelete").addEventListener("click", function () {
    if (state.variants.length <= 1) return;
    if (!window.confirm("«" + activeVariant().name + "» löschen?")) return;
    var gone = state.active;
    state.variants.splice(gone, 1);
    if (state.ref > gone) state.ref--;
    else if (state.ref === gone) state.ref = 0;
    normalizeShown();
    selectVariant(Math.max(0, gone - 1));
  });

  el("btnReset").addEventListener("click", function () {
    if (!window.confirm("Alle Varianten verwerfen und die Standardwerte der Excel-Vorlage laden?")) return;
    resetAll();
    fillPrices();
    selectVariant(0);
  });

  el("btnPdf").addEventListener("click", function () { window.print(); });
  el("btnExcel").addEventListener("click", function () { withXlsx(exportExcel); });
  el("btnSave").addEventListener("click", saveFile);
  el("btnOpen").addEventListener("click", function () { el("fileOpen").click(); });
  el("openNotice").addEventListener("click", function (e) {
    if (e.target.closest(".close")) this.hidden = true;
  });
  el("fileOpen").addEventListener("change", function () {
    if (this.files[0]) openFile(this.files[0]);
    this.value = "";
  });
  window.addEventListener("beforeprint", preparePrint);
  window.addEventListener("afterprint", restoreAfterPrint);

  el("chkOnlyDiff").addEventListener("change", function () {
    state.onlyDiff = this.checked;
    renderSpecTable(columns());
    save();
  });

  panel.addEventListener("click", function (e) {
    var t = e.target.closest(".secTab");
    if (t) selectTab(t.getAttribute("data-tab"));
    if (e.target.closest("#btnEbfAuto")) {
      activeVariant().inputs.ebfAuto = true;
      refresh();
    }
  });

  el("app").addEventListener("click", function (e) {
    var t = e.target.closest(".paneToggle");
    if (t) setPane(t.getAttribute("data-pane"), t.getAttribute("aria-expanded") !== "true");
  });

  /* Sortierung: absteigend, aufsteigend, dann wieder nach Lage im Gebäude */
  el("compareTable").addEventListener("click", function (e) {
    var b = e.target.closest(".sortBtn");
    if (!b) return;
    var key = b.getAttribute("data-sort"), s = state.sort;
    if (!key) state.sort = null;
    else if (!s || s.key !== key) state.sort = { key: key, dir: -1 };
    else state.sort = s.dir < 0 ? { key: key, dir: 1 } : null;
    var cols = columns();
    if (cols.length > 1) renderCompareTable(cols, groupItems());
    else renderSingleTable(cols[0], groupItems());
  });

  el("priceTable").addEventListener("input", function (e) {
    var k = e.target.getAttribute("data-price");
    if (!k) return;
    var raw = String(e.target.value).trim(), v = raw === "" ? null : parseFloat(raw.replace(",", "."));
    if (v !== null && !(isFinite(v) && v >= 0)) return;
    state.prices[k] = v;
    refresh();
  });

  el("btnPriceReset").addEventListener("click", function () {
    state.prices = defaultPrices();
    fillPrices();
    refresh();
  });

  el("groupingButtons").addEventListener("click", function (e) {
    var b = e.target.closest("[data-grouping]");
    if (!b) return;
    state.grouping = b.getAttribute("data-grouping");
    refresh();
  });


  var resizeTimer = null;
  window.addEventListener("resize", function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(renderOutputs, 200);
  });
}

function syncToggles() {
  document.querySelectorAll("#groupingButtons [data-grouping]").forEach(function (b) {
    b.classList.toggle("active", b.getAttribute("data-grouping") === state.grouping);
  });
  el("chkOnlyDiff").checked = state.onlyDiff;
}

/* ---------------------------------------------------------------------
   14. Start
   --------------------------------------------------------------------- */
function init(data) {
  DATA = data;
  if (!load()) resetAll();

  buildInputPanel();
  buildPriceTable();
  bindControls();
  fillInputs();
  fillPrices();
  syncToggles();

  el("sourceNote").textContent = "Bei «Benutzerdefiniert» lässt sich ein eigener GWP-Wert eingeben, z. B. aus einer Umweltproduktdeklaration (EPD). " +
    "Ökobilanzdaten " + DATA.kbob + ". Eingaben und Varianten werden nur lokal in diesem Browser gespeichert.";

  recompute();
  updateLookups();
  updateInputDecorations();
  renderVariantList();
  renderOutputs();
  renderPanes();

  el("loadingNote").style.display = "none";
  el("app").style.visibility = "visible";
}

document.addEventListener("DOMContentLoaded", function () {
  var url = document.body.getAttribute("data-json") || "data/co2rechner.json";
  fetch(url)
    .then(function (r) {
      if (!r.ok) throw new Error(r.status + " " + r.statusText);
      return r.json();
    })
    .then(init)
    .catch(function (err) {
      el("loadingNote").className = "alert alert-danger";
      el("loadingNote").textContent = "Die Daten konnten nicht geladen werden. (" + err.message + ")";
    });
});
